import { PLANET_HOPPERS_MODE, type PHView, type PHCommand } from "./planetHoppers";
import type { RelicCourseBounds } from "./relicRunCourse";
export { PLANET_HOPPERS_MODE } from "./planetHoppers";
export const ROOM_CODE_LENGTH = 2;
export const ROOM_CODE_MIN_LENGTH = ROOM_CODE_LENGTH;
export const ROOM_CODE_MAX_LENGTH = 8;
export const ROOM_CODE_LETTERS = "ABCDEFGHJKMNPQRSTUVWXYZ";
export const ROOM_CODE_DIGITS = "23456789";
export const MAX_NAME_LENGTH = 24;
export const MAX_SOURCE_TEXT_LENGTH = 5000;
export const MAX_PLAYERS = 64;
// Kept as one exact frame so Durable Object auto-response can answer health checks
// without waking the room or adding application-level heartbeat load.
export const ROOM_CONNECTION_HEARTBEAT_MESSAGE = '{"type":"connection-heartbeat"}';
export const MAZE_VISIBLE_RADIUS = 4;
// The larger tile field is scenery. Course words are revealed only three cells
// from the runner, even when a wide screen can show much more of the temple.
export const MAZE_REVEAL_RADIUS = 3;
export const isMazePositionRevealed = (position: GridPosition, runner: GridPosition): boolean =>
    Math.max(Math.abs(position.x - runner.x), Math.abs(position.y - runner.y)) <= MAZE_REVEAL_RADIUS;
// Keep one extra column on each side so the vignette hides the board edge instead of tile pop-in.
export const MAZE_VISIBLE_SIDE_OVERSCAN = 1;
export const PAPER_WHITE_AVATAR_HUE = -1;
export const DEFAULT_PLAYER_NAME = "Student";
export const DEFAULT_HOST_NAME = "Teacher";
export const ANONYMOUS_PLAYER_NAME_PREFIX = "Capybara";
// Shared room-mode contract for Worker logic, frontend rendering, i18n, and tests.
// Adding or moving a mode should update the full family instead of patching one surface.
export const RELIC_RUN_MODE = "relic-run" as const;
export const RELIC_RUN_LABEL = "Relic Run";
export const RELIC_RUN_DESCRIPTION = "Practice memorizing sentences by delving into the temple! Choose the right words!";
export const PICTURE_DETECTIVES_MODE = "picture-detectives" as const;
export const PICTURE_DETECTIVES_LABEL = "Picture Detectives";
export const PICTURE_DETECTIVES_DESCRIPTION =
    "Solve each case by guessing the drawn word from the case file.";
export const VAULT_BREAKERS_MODE = "vault-breakers" as const;
export const VAULT_BREAKERS_LABEL = "Vault Breakers";
export const VAULT_BREAKERS_DESCRIPTION =
    "Crack classroom vaults by choosing the right code before each scan resolves.";
export const DEFAULT_PICTIONARY_STEM = "I'm really interested in ____.";
export const PICTIONARY_LIVES_PER_ROUND = 5;
export const PICTIONARY_MIN_POINT_VALUE = 2;
export const PICTIONARY_MAX_POINT_VALUE = 25;
export const PICTIONARY_MIN_REVEAL_DURATION_SECONDS = 0;
export const PICTIONARY_MAX_REVEAL_DURATION_SECONDS = 80;
export const PICTIONARY_DEFAULT_REVEAL_DURATION_SECONDS = 50;
export const PICTIONARY_REVEAL_DURATION_STEP_SECONDS = 5;
export const PICTIONARY_ROUND_DURATION_SECONDS = 100;
export const PICTIONARY_ROUND_SUBMIT_GRACE_MS = 1000;
export const PICTIONARY_ROUND_TIMER_NUMERAL_FLASH_SECONDS = [75, 50, 25] as const;
export const PICTIONARY_ROUND_TIMER_GRACE_START_SECONDS = 11;
export const PICTIONARY_HINT_MODE_VALUES = ["over-time", "all-at-once", "off"] as const;
export const PICTIONARY_OBSERVE_ONLY_SECONDS = 3;
export const PICTIONARY_HIGH_RISK_GUESS_WINDOW_SECONDS = 20;
export const PICTIONARY_MEDIUM_RISK_GUESS_WINDOW_SECONDS = 40;
export const PICTIONARY_LOW_RISK_GUESS_WINDOW_SECONDS = 60;
export const PICTIONARY_SPEED_BONUS_TEAM_SHARE = 0.33;
export const PICTIONARY_FIRST_SOLVE_SPEED_BONUS_POINTS = 4;
export const PICTIONARY_SECOND_SOLVE_SPEED_BONUS_POINTS = 2;
export const PICTIONARY_SPEED_BONUS_POINTS = 1;
export const PICTIONARY_ZERO_HEART_SOLVE_BONUS = 1;
export const PICTIONARY_LUCKY_BREAK_MAX_PROMOTED_RANK = 3;
export const PICTIONARY_LUCKY_BREAK_MIN_ELIGIBLE_RANK = 4;
export const PICTIONARY_LUCKY_BREAK_THIRD_PLACE_BUFFER_POINTS = 1;
export const PICTIONARY_LUCKY_BREAK_MIN_DEFICIT_POINTS = 2;
export const PICTIONARY_LUCKY_BREAK_BASE_CHANCE = 0.15;
export const PICTIONARY_LUCKY_BREAK_CHANCE_PER_DEFICIT_POINT = 0.05;
export const PICTIONARY_LUCKY_BREAK_MAX_CHANCE = 0.7;
export const PICTIONARY_LUCKY_BREAK_EFFORT_CHANCE_PER_GUESS = 0.1;
export const PICTIONARY_LUCKY_BREAK_EFFORT_MAX_CHANCE = 0.9;
export const PICTIONARY_LUCKY_BREAK_MIN_TARGET_SHARE = 0.55;
export const PICTIONARY_MAX_SEGMENT_POINTS = 48;
export const PICTIONARY_EFFORT_FEEDBACK_MESSAGE = "A detective never quits!";
export const VAULT_BREAKER_DEFAULT_VALUES = [100, 200, 300, 400, 500] as const;
export const VAULT_BREAKER_ARMING_SECONDS = 3;
export const VAULT_BREAKER_DEFAULT_CRACK_DURATION_SECONDS = 15;
export const VAULT_BREAKER_MIN_CRACK_DURATION_SECONDS = 3;
export const VAULT_BREAKER_MAX_CRACK_DURATION_SECONDS = 60;
export const VAULT_BREAKER_CRACK_DURATIONS_SECONDS = [15, 12, 9] as const;
export const VAULT_BREAKER_FINAL_CRACK_DURATION_SECONDS = 30;
export const VAULT_BREAKER_FINAL_MIN_PRIZE = 1000;
export const VAULT_BREAKER_FINAL_PRIZE_MARGIN = 500;
export const VAULT_BREAKER_LAST_QUESTION_MIN_VALUE = 500;
export const VAULT_BREAKER_CRACK_ZERO_COYOTE_MS = 1000;
export const VAULT_BREAKER_CRACK_GRACE_MS = 2000;
export const VAULT_BREAKER_CHOICE_COUNTS = [15, 13, 11] as const;
export const VAULT_BREAKER_MIN_CHOICE_COUNT = 4;
export const VAULT_BREAKER_VALUE_UNLOCK_THRESHOLDS = [4, 8, 11, 14] as const;
export const VAULT_BREAKER_EFFORT_POINT_SHARE = 0.25;
export const VAULT_BREAKER_CRACK_BONUS_SHARES = [0.3, 0.15, 0] as const;
export const VAULT_BREAKER_COMEBACK_MAX_PROMOTED_RANK = 3;
export const VAULT_BREAKER_COMEBACK_SCORE_BUFFER_POINTS = 10;
export const VAULT_BREAKER_COMEBACK_FAST_LOCK_MULTIPLIER = 3;
export const VAULT_BREAKER_COMEBACK_DEFICIT_SPEED_SHARE = 3;
export const VAULT_BREAKER_COMEBACK_MIN_DEFICIT_SHARE = 0.1;
export const VAULT_BREAKER_COMEBACK_MAX_DEFICIT_SHARE = 0.65;
export const VAULT_BREAKER_COMEBACK_TWO_TEAM_SCALE = 0.5;
export const VAULT_BREAKER_MASSIVE_LOOT_LABEL = "Massive Loot";
export const VAULT_BREAKER_SURGE_MULTIPLIER = 2;
export const VAULT_BREAKER_MASSIVE_LOOT_REVEAL_MS = 4000;
export const VAULT_BREAKER_SURGE_BASE_CHANCE = 0.2;
export const VAULT_BREAKER_SURGE_WARMUP_ROUNDS = 2;
export const VAULT_BREAKER_SURGE_CHANCE_PER_DEFICIT_STEP = 0.05;
export const VAULT_BREAKER_SURGE_DEFICIT_STEP_POINTS = 200;
export const VAULT_BREAKER_SURGE_MAX_CHANCE = 0.55;
export const VAULT_BREAKER_DIAMOND_LABEL = "Diamond";
export const VAULT_BREAKER_DIAMOND_BONUS_POINTS = 200;
export const VAULT_BREAKER_DIAMOND_REVEAL_MS = 4500;
export const VAULT_BREAKER_DIAMOND_MIN_CLEARED_COUNT = 4;
export const VAULT_BREAKER_DIAMOND_BASE_CHANCE = 0.04;
export const VAULT_BREAKER_DIAMOND_NEGLECT_CHANCE = 0.02;
export const VAULT_BREAKER_DIAMOND_MAX_CHANCE = 0.22;
export const VAULT_BREAKER_DIAMOND_WEIGHT_BASE = 1;
export const VAULT_BREAKER_DIAMOND_NEGLECT_START_ROUNDS = 4;
export const VAULT_BREAKER_DIAMOND_NEGLECT_POWER = 5;
export const VAULT_BREAKER_DIAMOND_STACK_PRESSURE_DAMPING = 10;
export const VAULT_BREAKER_DIAMOND_STACK_WEIGHT_DAMPING = 8;
export const VAULT_BREAKER_GROWING_STASH_BONUS_POINTS = 100;
export const VAULT_BREAKER_GROWING_STASH_MAX_BONUS_POINTS = 400;
export const VAULT_BREAKER_GROWING_STASH_REVEAL_MS = 3600;
export const VAULT_BREAKER_GROWING_STASH_MIN_UNLOCKED_VALUE_INDEX = 1;
export const VAULT_BREAKER_GROWING_STASH_BASE_CHANCE = 0.07;
export const VAULT_BREAKER_GROWING_STASH_TIER_GAP_CHANCE = 0.08;
export const VAULT_BREAKER_GROWING_STASH_PROGRESS_STEP_CLEARS = 3;
export const VAULT_BREAKER_GROWING_STASH_PROGRESS_CHANCE = 0.03;
export const VAULT_BREAKER_GROWING_STASH_MAX_CHANCE = 0.35;
export const DEFAULT_PARTICIPANT_AWAY_AFTER_MS = 30_000;
export const PARTICIPANT_AWAY_GRACE_MS = 10_000;
export const PICTIONARY_PARTICIPANT_AWAY_AFTER_MS = 25_000;
// Visible Vault players earn Away through missed whole vaults, never a tap timer.
export const VAULT_BREAKERS_INACTIVE_ROUNDS_BEFORE_AWAY = 2;
export const VAULT_BREAKERS_PARTICIPANT_AWAY_AFTER_MS = Number.POSITIVE_INFINITY;
export const VAULT_BREAKERS_LEFT_GRACE_MS = 5_000;
export const VAULT_BREAKERS_HIDDEN_AWAY_GRACE_MS = VAULT_BREAKERS_LEFT_GRACE_MS;
export const getParticipantAwayAfterMs = (phase: RoomPhase, mode: RoomMode | null): number => {
    // Reading a question or listening to the teacher is normal participation.
    if (mode === "planet-hoppers") return 5 * 60_000;
    if (mode === VAULT_BREAKERS_MODE) {
        return VAULT_BREAKERS_PARTICIPANT_AWAY_AFTER_MS;
    }

    const baseMs =
        phase === "active" && mode === PICTURE_DETECTIVES_MODE
            ? PICTIONARY_PARTICIPANT_AWAY_AFTER_MS
            : DEFAULT_PARTICIPANT_AWAY_AFTER_MS;

    return baseMs + PARTICIPANT_AWAY_GRACE_MS;
};
export const getParticipantHiddenAwayAfterMs = (_phase: RoomPhase, mode: RoomMode | null): number =>
    mode === VAULT_BREAKERS_MODE ? VAULT_BREAKERS_LEFT_GRACE_MS : PARTICIPANT_AWAY_GRACE_MS;
export const getParticipantDisconnectedAwayAfterMs = (mode: RoomMode | null): number =>
    mode === "planet-hoppers" ? PARTICIPANT_AWAY_GRACE_MS : mode === VAULT_BREAKERS_MODE ? VAULT_BREAKERS_LEFT_GRACE_MS : 0;
const MAX_PICTIONARY_STEM_LENGTH = 160;
const MAX_PICTIONARY_CELL_LENGTH = 160;
const MAX_VAULT_BREAKER_CATEGORY_LENGTH = 80;
const MAX_VAULT_BREAKER_PROMPT_LENGTH = 220;
const MAX_VAULT_BREAKER_ANSWER_LENGTH = 80;
const UNICODE_WORD_CHAR_SOURCE = String.raw`\p{L}\p{N}`;
const FALLBACK_WORD_CHAR_SOURCE = "A-Za-z0-9\\u00C0-\\u024F\\u1100-\\u11FF\\u3130-\\u318F\\uAC00-\\uD7AF";
const createCompatibleRegExp = (unicodeSource: string, flags: string, fallbackSource: string): RegExp => {
    try {
        return new RegExp(unicodeSource, flags);
    } catch {
        return new RegExp(fallbackSource, flags.replace(/u/g, ""));
    }
};
const SOURCE_WORD_PATTERN = createCompatibleRegExp(
    `[${UNICODE_WORD_CHAR_SOURCE}]+(?:['\\u2019\\\`\\-][${UNICODE_WORD_CHAR_SOURCE}]+)*(?:[.,!?;:\\u2026)"'\\]]+)?`,
    "gu",
    `[${FALLBACK_WORD_CHAR_SOURCE}]+(?:['\\u2019\\\`\\-][${FALLBACK_WORD_CHAR_SOURCE}]+)*(?:[.,!?;:\\u2026)"'\\]]+)?`
);
const PICTIONARY_STEM_WORD_PATTERN = createCompatibleRegExp(
    `%?[${UNICODE_WORD_CHAR_SOURCE}]+(?:['\\u2019\\\`\\-][${UNICODE_WORD_CHAR_SOURCE}]+)*%?`,
    "gu",
    `%?[${FALLBACK_WORD_CHAR_SOURCE}]+(?:['\\u2019\\\`\\-][${FALLBACK_WORD_CHAR_SOURCE}]+)*%?`
);
const PICTIONARY_FIRST_STEM_REVEAL_RATIO = 0.38;
const PICTIONARY_FIRST_STEM_REVEAL_MIN_DELAY_MS = 2500;
const PICTIONARY_FIRST_STEM_REVEAL_MAX_DELAY_MS = 6000;
const WORD_EDGE_TRIM_PATTERN = createCompatibleRegExp(
    `^[^${UNICODE_WORD_CHAR_SOURCE}]+|[^${UNICODE_WORD_CHAR_SOURCE}]+$`,
    "gu",
    `^[^${FALLBACK_WORD_CHAR_SOURCE}]+|[^${FALLBACK_WORD_CHAR_SOURCE}]+$`
);
const NON_WORD_GLOBAL_PATTERN = createCompatibleRegExp(
    `[^${UNICODE_WORD_CHAR_SOURCE}]+`,
    "gu",
    `[^${FALLBACK_WORD_CHAR_SOURCE}]+`
);
const ROOM_CODE_INPUT_SCAN_LIMIT = ROOM_CODE_MAX_LENGTH * 4;
const ROOM_CODE_MAX_LETTERS = Math.floor(ROOM_CODE_MAX_LENGTH / 2);
const ROOM_CODE_MAX_DIGITS = ROOM_CODE_MAX_LENGTH - ROOM_CODE_MAX_LETTERS;

export type RoomMode =
    | typeof RELIC_RUN_MODE
    | typeof PICTURE_DETECTIVES_MODE
    | typeof VAULT_BREAKERS_MODE
    | typeof PLANET_HOPPERS_MODE;
export type RoomPhase = "lobby" | "active" | "results";
export type PlayerEventKind = "connected" | "reset" | "advance" | "complete";
export type MazeTileKind = "start" | "end" | "portal" | "path" | "trail" | "decoy";
export type PictionaryHintMode = (typeof PICTIONARY_HINT_MODE_VALUES)[number];
export type VaultBreakerCrackNumber = 1 | 2 | 3;

// A host fallback is a retry against one observed crack, not permission to advance whichever crack is current.
export interface VaultBreakerCrackAdvanceIdentity {
    roundNumber: number;
    vaultId: string;
    crackNumber: VaultBreakerCrackNumber;
    crackStartedAt: number | null;
}

export const parseVaultBreakerCrackAdvanceIdentity = (value: unknown): VaultBreakerCrackAdvanceIdentity | null => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }

    const candidate = value as Partial<VaultBreakerCrackAdvanceIdentity>;
    const roundNumber = candidate.roundNumber;
    const vaultId = candidate.vaultId;
    const crackNumber = candidate.crackNumber;
    const crackStartedAt = candidate.crackStartedAt;

    if (
        typeof roundNumber !== "number" ||
        !Number.isInteger(roundNumber) ||
        roundNumber < 1 ||
        typeof vaultId !== "string" ||
        vaultId.length === 0 ||
        (crackNumber !== 1 && crackNumber !== 2 && crackNumber !== 3) ||
        (crackStartedAt !== null &&
            (typeof crackStartedAt !== "number" || !Number.isFinite(crackStartedAt)))
    ) {
        return null;
    }

    return {
        roundNumber,
        vaultId,
        crackNumber,
        crackStartedAt: crackStartedAt === null ? null : Math.trunc(crackStartedAt)
    };
};

export const matchesVaultBreakerCrackAdvanceIdentity = (
    expected: VaultBreakerCrackAdvanceIdentity | null,
    current: VaultBreakerCrackAdvanceIdentity
): boolean =>
    expected !== null &&
    expected.roundNumber === current.roundNumber &&
    expected.vaultId === current.vaultId &&
    expected.crackNumber === current.crackNumber &&
    expected.crackStartedAt === current.crackStartedAt;

export const DEFAULT_PICTIONARY_HINT_MODE: PictionaryHintMode = "over-time";

export const isPictionaryHintMode = (value: unknown): value is PictionaryHintMode =>
    typeof value === "string" &&
    (PICTIONARY_HINT_MODE_VALUES as readonly string[]).includes(value);

export const clampPictionaryPointValue = (value: number): number => {
    const integerValue = Number.isFinite(value) ? Math.trunc(value) : PICTIONARY_MIN_POINT_VALUE;

    return Math.min(PICTIONARY_MAX_POINT_VALUE, Math.max(PICTIONARY_MIN_POINT_VALUE, integerValue));
};

export const getPictionaryDefaultNextPointValue = (roundNumber: number): number => {
    const normalizedRoundNumber = Number.isFinite(roundNumber) ? Math.max(0, Math.trunc(roundNumber)) : 0;

    return clampPictionaryPointValue(normalizedRoundNumber + 2);
};

export const getPictionaryRoundTimerNumeral = (remainingMs: number): number | null => {
    const displaySeconds = Math.ceil(Math.max(0, remainingMs) / 1000);

    if (displaySeconds <= 0) {
        return null;
    }

    if (displaySeconds <= 1) {
        return 0;
    }

    if (displaySeconds <= PICTIONARY_ROUND_TIMER_GRACE_START_SECONDS) {
        return Math.ceil((displaySeconds - 1) / 2);
    }

    return (PICTIONARY_ROUND_TIMER_NUMERAL_FLASH_SECONDS as readonly number[]).includes(displaySeconds)
        ? displaySeconds
        : null;
};

export const getPictionaryRoundAutoSubmitAt = (roundStartedAt: number | null): number | null => {
    if (roundStartedAt === null || PICTIONARY_ROUND_DURATION_SECONDS <= 0) {
        return null;
    }

    return roundStartedAt + PICTIONARY_ROUND_DURATION_SECONDS * 1000 + PICTIONARY_ROUND_SUBMIT_GRACE_MS;
};

export interface GridPosition {
    x: number;
    y: number;
}

export interface MazeWordToken {
    display: string;
    normalized: string;
}

export interface RelicRunLevelDefinition {
    sourceText: string;
    tokenCount: number;
    tokens: MazeWordToken[];
}

export interface RelicRunLevelView {
    index: number;
    sourceText: string;
    tokenCount: number;
}

export interface MazeTileView extends GridPosition {
    key: string;
    word: string;
    kind: MazeTileKind;
    isCurrent: boolean;
    isReachable: boolean;
    isNextStep: boolean;
}

export interface PlayerEventView {
    id: number;
    kind: PlayerEventKind;
    message: string;
}

export interface PlayerMazeView {
    sessionId: string;
    playerName: string;
    isViewerControlled: boolean;
    position: GridPosition;
    entryCorner?: "top-left" | "bottom-left";
    courseBounds?: RelicCourseBounds;
    coursePlacement?: { x: number; y: number };
    avatarHue: number;
    levelNumber: number;
    levelCount: number;
    isFinalLevel: boolean;
    progressIndex: number;
    completedCount: number;
    totalWords: number;
    currentWord: string | null;
    nextWord: string | null;
    passageText: string;
    completed: boolean;
    canReplay: boolean;
    runElapsedMs: number;
    runTimerStartedAt: number | null;
    runPersonalBestMs: number | null;
    lastFinishedRunMs: number | null;
    levelBestImprovementMs: number | null;
    lastEvent: PlayerEventView | null;
    visibleTiles: MazeTileView[];
}

export interface PictionaryEntryDefinition {
    english: string;
    korean: string;
    stem: string;
    normalizedEnglish: string;
}

export interface PictionaryEntryView {
    index: number;
    english: string;
    korean: string;
    stem: string;
    timesUsed: number;
}

export interface PictionaryWordBankEntryView {
    english: string;
    korean: string;
}

export interface PictionarySourceRow {
    english: string;
    korean: string;
    stem: string;
}

export interface PictionaryBuildOptions {
    fallbackStem?: string;
    noTranslations?: boolean;
    noHints?: boolean;
    sameStem?: boolean;
}

export interface PictionaryCanvasPoint {
    x: number;
    y: number;
}

export interface PictionaryStrokeView {
    id: string;
    points: PictionaryCanvasPoint[];
}

export interface PictionaryTeamView {
    sessionId: string;
    name: string;
    renamePending?: boolean;
    connected: boolean;
    isAway: boolean;
    awayReason: "hidden" | "idle" | "disconnected" | null;
    score: number;
    livesRemaining: number;
    solved: boolean;
    solvedRank: number | null;
    cooldownEndsAt: number | null;
}

export interface PictionaryRoundCompletionTeamStatus {
    connected: boolean;
    solved: boolean;
    livesRemaining?: number;
}

export const shouldAutoCompletePictionaryRound = (
    teams: readonly PictionaryRoundCompletionTeamStatus[]
): boolean => {
    const connectedTeams = teams.filter((team) => team.connected);

    if (connectedTeams.length === 0) {
        return false;
    }

    // Zero-try teams can keep guessing after cooldown, so tries are not completion state.
    return connectedTeams.every((team) => team.solved);
};

export type PictionaryFeedbackKind = "wrong" | "misspelling" | "correct" | "info";

export interface PictionaryAwardView {
    scoreDelta: number;
    casePoints: number;
    speedBonusPoints: number;
    breakthroughBonusPoints: number;
    effortPoints: number;
    zeroHeartSolvePoints: number;
}

export interface PictionaryViewerStateView {
    /** Server-owned personal recharge, frozen for this case; absent on legacy cases. */
    translationRechargeMs?: number;
    /** Legacy case-start rank, retained for older clients and in-flight upgrades. */
    translationRankFraction?: number;
    score: number;
    livesRemaining: number;
    solved: boolean;
    observeEndsAt: number | null;
    cooldownEndsAt: number | null;
    canGuess: boolean;
    lastFeedback: string | null;
    lastFeedbackKind: PictionaryFeedbackKind | null;
    lastFeedbackSpeedBonus: boolean;
    lastFeedbackSpeedBonusPoints: number;
    lastAward: PictionaryAwardView | null;
    breakthroughBonusAvailable: boolean;
    breakthroughEffortActive: boolean;
}

export interface PictionaryRoomView {
    vocabularyReview?: { open: boolean; index: number };
    stem: string;
    guessStem: string;
    entryCount: number;
    noHints: boolean;
    noTranslations: boolean;
    pointValue: number;
    currentWorth: number;
    nextWorth: number;
    nextWorthManual: boolean;
    revealDurationSeconds: number;
    hintMode: PictionaryHintMode;
    roundNumber: number;
    roundActive: boolean;
    roundComplete: boolean;
    roundStartedAt: number | null;
    roundCompletedAt: number | null;
    stemRevealOrder: number[];
    controllerName: string | null;
    controllerConnected: boolean;
    controllerIsHost: boolean;
    controllerReleaseAt: number | null;
    currentWordIndex: number | null;
    currentWordEnglish: string | null;
    currentWordKorean: string | null;
    entries: PictionaryEntryView[];
    wordBank: PictionaryWordBankEntryView[];
    teams: PictionaryTeamView[];
    viewer: PictionaryViewerStateView | null;
    strokes: PictionaryStrokeView[];
}

export interface VaultBreakerEntryDefinition {
    id: string;
    category: string;
    categoryIndex: number;
    value: number;
    valueIndex: number;
    prompt: string;
    answer: string;
    korean: string;
    normalizedAnswer: string;
}

export type VaultBreakerSourceGuideLine =
    | {
          kind: "empty";
          lineIndex: number;
      }
    | {
          kind: "category";
          lineIndex: number;
          category: string;
          categoryIndex: number;
          entryCount: number;
      }
    | {
          kind: "vault";
          lineIndex: number;
          category: string;
          categoryIndex: number;
          value: number;
          valueIndex: number;
          answer: string;
          korean: string;
      };

export interface VaultBreakerCategoryView {
    index: number;
    title: string;
}

export interface VaultBreakerTileView {
    id: string;
    categoryIndex: number;
    valueIndex: number;
    value: number;
    pointMultiplier: number;
    diamondBonusPoints: number;
    growingStashBonusPoints: number;
    prompt: string;
    answer: string | null;
    korean: string | null;
    cleared: boolean;
    locked: boolean;
}

export interface VaultBreakerChoiceView {
    id: string;
    answer: string;
    korean: string;
    selectable: boolean;
    lockRank: number | null;
    lockCount: number;
}

export type VaultBreakerFeedbackKind = "correct" | "wrong" | "info";

export interface VaultBreakerAwardView {
    scoreDelta: number;
    basePoints: number;
    earlyBonusPoints: number;
    comebackBonusPoints: number;
    effortPoints: number;
    pointMultiplier: number;
    diamondBonusPoints: number;
    growingStashBonusPoints: number;
    correct: boolean;
    crackNumber: VaultBreakerCrackNumber;
    lockRank: number | null;
    lockCount: number;
    finalVault?: boolean;
}

export interface VaultBreakerTeamView {
    sessionId: string;
    name: string;
    renamePending?: boolean;
    connected: boolean;
    isAway: boolean;
    awayReason: "hidden" | "idle" | "disconnected" | null;
    placement?: PlayerRosterPlacement | null;
    score: number;
    solved: boolean;
    selectedChoiceId: string | null;
    selectedLockRank?: number | null;
    selectedLockCount?: number;
    submittedCracks: number[];
    lastAward?: VaultBreakerAwardView | null;
}

export type VaultBreakerCrackStatus = "locked" | "active" | "failed" | "cracked";

export interface VaultBreakerCrackView {
    crackNumber: VaultBreakerCrackNumber;
    status: VaultBreakerCrackStatus;
}

export interface VaultBreakerViewerStateView {
    /** Personal reveal window as a fraction of this crack, including zero and full support. */
    translationDurationFraction?: number;
    /** Legacy per-crack rank, retained for older clients and in-flight upgrades. */
    translationRankFraction?: number;
    score: number;
    solved: boolean;
    canSelect: boolean;
    selectedChoiceId: string | null;
    submittedCracks: number[];
    lastFeedback: string | null;
    lastFeedbackKind: VaultBreakerFeedbackKind | null;
    lastAward: VaultBreakerAwardView | null;
    choices: VaultBreakerChoiceView[];
}

export interface VaultBreakerRoomView {
    categories: VaultBreakerCategoryView[];
    tiles: VaultBreakerTileView[];
    entryCount: number;
    clearedCount: number;
    remainingVaultCount: number;
    unlockedValueIndex: number;
    finalVaultState: VaultBreakerFinalState;
    finalVaultPrize: number;
    finalVaultEligibleValue: number | null;
    finalVaultCrackDurationSeconds: number;
    roundNumber: number;
    currentVaultId: string | null;
    currentVault: VaultBreakerTileView | null;
    pointMultiplier: number;
    diamondActive: boolean;
    diamondCount: number;
    diamondBonusPoints: number;
    diamondRevealEndsAt: number | null;
    diamondVaultId: string | null;
    diamondAwardedAt: number | null;
    diamondCategoryIndex: number | null;
    diamondCategoryTitle: string | null;
    diamondAwardedPoints: number;
    diamondTotalPoints: number;
    growingStashActive: boolean;
    growingStashBonusPoints: number;
    growingStashRevealEndsAt: number | null;
    growingStashVaultId: string | null;
    growingStashAwardedAt?: number | null;
    growingStashCategoryIndex?: number | null;
    growingStashCategoryTitle?: string | null;
    growingStashAwardedPoints?: number;
    growingStashTotalPoints?: number;
    chooserSessionId: string | null;
    chooserName: string | null;
    chooserAvatarHue?: number | null;
    crackNumber: VaultBreakerCrackNumber | null;
    roundActive: boolean;
    roundComplete: boolean;
    armingEndsAt: number | null;
    massiveLootRevealEndsAt: number | null;
    crackDurationSeconds: number;
    crackStartedAt: number | null;
    crackEndsAt: number | null;
    crackResolveAt: number | null;
    roundCompletedAt: number | null;
    cracks: VaultBreakerCrackView[];
    teams: VaultBreakerTeamView[];
    viewer: VaultBreakerViewerStateView | null;
}

export type VaultBreakerFinalState = "idle" | "armed" | "active" | "complete";

export type PlayerRosterPlacement = 1 | 2 | 3;

export interface RoomParticipantView {
    sessionId: string;
    name: string;
    isHost?: boolean;
    renamePending?: boolean;
    connected: boolean;
    completed: boolean;
    isController: boolean;
    placement?: PlayerRosterPlacement | null;
    relicRunBestMs?: number | null;
    isAway?: boolean;
    awayReason?: "hidden" | "idle" | "disconnected" | null;
}

export type RoomPlayerHistoryEventKind =
    | "join"
    | "reconnect"
    | "rename"
    | "rename-request"
    | "disconnect"
    | "kick";

export type RoomPlayerHistoryRole = "host" | "player" | "controller";

export interface RoomPlayerHistoryEventView {
    id: number;
    recordedAt: number;
    kind: RoomPlayerHistoryEventKind;
    sessionId: string;
    role: RoomPlayerHistoryRole;
    nameBefore?: string | null;
    nameAfter?: string | null;
    inputName?: string | null;
    actorSessionId?: string | null;
    actorName?: string | null;
}

type RelicRunTimedParticipant = Pick<RoomParticipantView, "sessionId" | "name" | "relicRunBestMs">;

const normalizeRelicRunParticipantTimeMs = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) && value >= 0
        ? Math.round(value)
        : null;

export const compareRelicRunParticipantTimes = (
    left: RelicRunTimedParticipant,
    right: RelicRunTimedParticipant
): number => {
    const leftTimeMs = normalizeRelicRunParticipantTimeMs(left.relicRunBestMs);
    const rightTimeMs = normalizeRelicRunParticipantTimeMs(right.relicRunBestMs);

    if (leftTimeMs === null && rightTimeMs === null) {
        return 0;
    }

    if (leftTimeMs === null) {
        return 1;
    }

    if (rightTimeMs === null) {
        return -1;
    }

    return (
        leftTimeMs - rightTimeMs ||
        left.name.localeCompare(right.name) ||
        left.sessionId.localeCompare(right.sessionId)
    );
};

export const orderRelicRunParticipantsByTime = <T extends RelicRunTimedParticipant>(participants: readonly T[]): T[] =>
    [...participants].sort(compareRelicRunParticipantTimes);

export interface FinalResultCandidateView {
    sessionId: string;
    name: string;
    connected: boolean;
    score: number | null;
    elapsedMs: number | null;
    sortIndex?: number;
}

export interface FinalResultPlayerView {
    sessionId: string;
    name: string;
    connected: boolean;
    score: number | null;
    elapsedMs: number | null;
}

export interface FinalResultStandingView extends FinalResultPlayerView {
    placement: number | null;
}

export interface FinalResultGroupView {
    placement: PlayerRosterPlacement;
    score: number | null;
    elapsedMs: number | null;
    players: FinalResultPlayerView[];
}

export interface RoomFinalResultsView {
    mode: RoomMode;
    startedAt: number | null;
    groups: FinalResultGroupView[];
    standings: FinalResultStandingView[];
    competitorCount: number;
    qualifierCount: number;
}

export interface LobbyPositionView {
    x: number;
    y: number;
}

export interface LobbyPelletView {
    id: string;
    x: number;
    y: number;
}

export interface LobbyPlayerView {
    sessionId: string;
    name: string;
    connected: boolean;
    avatarHue: number;
    position: LobbyPositionView;
    score: number;
}

export interface LobbyPlaygroundView {
    players: LobbyPlayerView[];
    pellets: LobbyPelletView[];
}

export interface ViewerInfo {
    sessionId: string;
    name: string;
    renamePending?: boolean;
    avatarHue?: number;
    isHost: boolean;
    isController: boolean;
}

export interface HostSetupView {
    selectedMode: RoomMode | null;
    sourceText: string;
    singleLineMode: boolean;
    hostParticipates: boolean;
    tokenCount: number;
    levelCount: number;
    levels: RelicRunLevelView[];
    pictionaryStem: string;
    pictionaryEntryCount: number;
    pictionaryNoHints: boolean;
    pictionaryNoTranslations: boolean;
    pictionarySameStem: boolean;
    pictionaryHostIsController: boolean;
    pictionaryControllerConnected: boolean;
    pictionaryControllerName: string | null;
    vaultEntryCount: number;
    canStart: boolean;
}

export interface RoomView {
    roomCode: string;
    activityId?: string;
    activityStartedAt?: number;
    serverNow?: number;
    phase: RoomPhase;
    mode: RoomMode | null;
    modeLabel: string | null;
    modeDescription: string | null;
    viewer: ViewerInfo;
    participants: RoomParticipantView[];
    playerHistory?: RoomPlayerHistoryEventView[];
    lobbyPlayground: LobbyPlaygroundView | null;
    connectedCount: number;
    playerCount: number;
    started: boolean;
    setup: HostSetupView;
    player: PlayerMazeView | null;
    spectatorPlayers: PlayerMazeView[];
    pictionary: PictionaryRoomView | null;
    vaultBreakers: VaultBreakerRoomView | null;
    planetHoppers?: PHView | null;
    finalResults?: RoomFinalResultsView | null;
    statusMessage: string;
}

export interface CreateRoomResponse {
    roomCode: string;
    hostToken: string;
    hostUrl: string;
    joinUrl: string;
}

export interface RoomSummaryResponse {
    exists: boolean;
    roomCode: string;
    phase: RoomPhase;
    mode: RoomMode | null;
    started: boolean;
    connectedCount: number;
    connectedPlayerCount?: number;
    playerCount?: number;
    lobbyPlayerCount?: number;
}

export interface RoomStateMessage {
    type: "room-state";
    room: RoomView;
}

export interface LobbyStateMessage {
    type: "lobby-state";
    roomCode: string;
    lobby: LobbyPlaygroundView;
}

export interface PictionaryCanvasSegmentMessage {
    type: "pictionary-canvas-segment";
    roomCode: string;
    strokeId: string;
    begin: boolean;
    roundNumber: number;
    currentWordIndex: number | null;
    roundStartedAt: number | null;
    points: PictionaryCanvasPoint[];
}

export interface ErrorMessage {
    type: "error";
    message: string;
}

export interface RenameRequestMessage {
    type: "rename-request";
    roomCode: string;
}

export interface ConnectionHeartbeatMessage {
    type: "connection-heartbeat";
}

export type ServerMessage =
    | RoomStateMessage
    | LobbyStateMessage
    | PictionaryCanvasSegmentMessage
    | RenameRequestMessage
    | ConnectionHeartbeatMessage
    | ErrorMessage;

export interface HostConfigureMessage {
    type: "host-configure";
    payload: {
        mode: RoomMode | null;
        sourceText: string;
        singleLineMode: boolean;
        hostParticipates: boolean;
        pictionaryStem: string;
        pictionaryNoHints: boolean;
        pictionaryNoTranslations: boolean;
        pictionarySameStem: boolean;
        pictionaryHostIsController: boolean;
    };
}

export interface HostSetParticipationMessage {
    type: "host-set-participation";
    payload: {
        participates: boolean;
    };
}

export interface HostStartMessage {
    type: "host-start";
}

export interface HostEndMessage {
    type: "host-end";
    payload?: {
        action?: "show-results" | "return-lobby";
    };
}

export interface MazeMoveMessage {
    type: "maze-move";
    payload: GridPosition;
}

export interface MazeReplayMessage {
    type: "maze-replay";
}

export interface MazeReadyMessage {
    type: "maze-ready";
}

export interface LobbyMoveMessage {
    type: "lobby-move";
    payload: LobbyPositionView;
}

export interface RenamePlayerMessage {
    type: "rename-player";
    payload: {
        name: string;
        targetSessionId?: string;
    };
}

export interface HostPromptRenameMessage {
    type: "host-prompt-rename";
    payload: {
        targetSessionId: string;
    };
}

export interface HostKickPlayerMessage {
    type: "host-kick-player";
    payload: {
        targetSessionId: string;
    };
}

export type BrowserTranslationStatus = "page" | "content" | null;
export interface PictionaryTranslationUsageMessage {
    type: "pictionary-translation-usage";
    payload: { roundStartedAt: number; count: number; firstAt: number | null; complete: boolean; revealMs: number; rechargeMs: number;
        rule?: "guess-efficiency-v1" | "rank-v1" };
}

export interface ViewerTranslationMessage {
    type: "viewer-translation";
    payload: { status: BrowserTranslationStatus };
}

export interface ViewerPresenceMessage {
    type: "viewer-presence";
    payload: {
        hidden: boolean;
        interacted: boolean;
    };
}

export interface PictionaryGuessMessage {
    type: "pictionary-guess";
    payload: {
        guess: string;
    };
}

export interface PictionaryNextRoundMessage {
    type: "pictionary-next-round";
    payload: {
        entryIndex: number;
        pointValue: number;
        revealDurationSeconds: number;
        hintMode: PictionaryHintMode;
    };
}

export interface PictionaryPreviewWorthMessage {
    type: "pictionary-preview-worth";
    payload: {
        pointValue: number;
        manual: boolean;
    };
}

export interface PictionaryVocabularyReviewMessage {
    type: "pictionary-vocabulary-review";
    payload: { open: boolean; index: number };
}

export interface PictionaryEndRoundMessage {
    type: "pictionary-end-round";
}

export interface PictionaryAdjustScoreMessage {
    type: "pictionary-adjust-score";
    payload: {
        targetSessionId: string;
        delta: number;
    };
}

export interface PictionaryDrawMessage {
    type: "pictionary-draw";
    payload: {
        strokeId: string;
        begin: boolean;
        end?: boolean;
        points: PictionaryCanvasPoint[];
    };
}

export interface RoomResyncMessage {
    type: "room-resync";
}

export interface PictionaryUndoStrokeMessage {
    type: "pictionary-undo-stroke";
}

export interface PictionaryClearCanvasMessage {
    type: "pictionary-clear-canvas";
}

export interface VaultBreakerChooseVaultMessage {
    type: "vault-choose";
    payload: {
        vaultId: string;
    };
}

export interface VaultBreakerSelectChoiceMessage {
    type: "vault-select";
    payload: {
        choiceId: string;
    };
}

export interface VaultBreakerNextCrackMessage {
    type: "vault-next-crack";
    payload: VaultBreakerCrackAdvanceIdentity;
}

export interface VaultBreakerFinishRoundMessage {
    type: "vault-finish-round";
}

export interface VaultBreakerRollChooserMessage {
    type: "vault-roll-chooser";
}

export interface VaultBreakerArmFinalMessage {
    type: "vault-arm-final";
}

export interface VaultBreakerToggleFinalMessage {
    type: "vault-toggle-final";
}

export interface VaultBreakerUnlockNextValueMessage {
    type: "vault-unlock-next-value";
}

export interface VaultBreakerAdjustCrackDurationMessage {
    type: "vault-adjust-crack-duration";
    payload: {
        durationSeconds: number;
    };
}

export interface VaultBreakerAdjustScoreMessage {
    type: "vault-adjust-score";
    payload: {
        targetSessionId: string;
        delta: number;
    };
}

export type ClientMessage =
    | PHCommand
    | HostConfigureMessage
    | HostSetParticipationMessage
    | HostStartMessage
    | HostEndMessage
    | MazeMoveMessage
    | MazeReplayMessage
    | MazeReadyMessage
    | LobbyMoveMessage
    | RenamePlayerMessage
    | HostPromptRenameMessage
    | HostKickPlayerMessage
    | ConnectionHeartbeatMessage
    | ViewerTranslationMessage
    | PictionaryTranslationUsageMessage
    | ViewerPresenceMessage
    | RoomResyncMessage
    | PictionaryGuessMessage
    | PictionaryNextRoundMessage
    | PictionaryPreviewWorthMessage
    | PictionaryVocabularyReviewMessage
    | PictionaryEndRoundMessage
    | PictionaryAdjustScoreMessage
    | PictionaryDrawMessage
    | PictionaryUndoStrokeMessage
    | PictionaryClearCanvasMessage
    | VaultBreakerChooseVaultMessage
    | VaultBreakerSelectChoiceMessage
    | VaultBreakerNextCrackMessage
    | VaultBreakerFinishRoundMessage
    | VaultBreakerRollChooserMessage
    | VaultBreakerArmFinalMessage
    | VaultBreakerToggleFinalMessage
    | VaultBreakerUnlockNextValueMessage
    | VaultBreakerAdjustCrackDurationMessage
    | VaultBreakerAdjustScoreMessage;

export const getRoomCodeLetterCount = (length: number): number =>
    Math.floor(length / 2);

export const getRoomCodeDigitCount = (length: number): number =>
    length - getRoomCodeLetterCount(length);

const isRoomCodeLetter = (character: string): boolean =>
    ROOM_CODE_LETTERS.includes(character);

const isRoomCodeDigit = (character: string): boolean =>
    ROOM_CODE_DIGITS.includes(character);

export const createRoomCodeForLength = (length: number): string => {
    const normalizedLength = Math.trunc(length);

    if (
        normalizedLength < ROOM_CODE_MIN_LENGTH ||
        normalizedLength > ROOM_CODE_MAX_LENGTH
    ) {
        throw new Error("Unsupported room code length.");
    }

    const letterCount = getRoomCodeLetterCount(normalizedLength);
    const digitCount = getRoomCodeDigitCount(normalizedLength);
    const bytes = new Uint8Array(normalizedLength);
    crypto.getRandomValues(bytes);

    const letters = Array.from(
        { length: letterCount },
        (_, index) => ROOM_CODE_LETTERS[bytes[index] % ROOM_CODE_LETTERS.length]
    ).join("");
    const digits = Array.from(
        { length: digitCount },
        (_, index) =>
            ROOM_CODE_DIGITS[
                bytes[letterCount + index] % ROOM_CODE_DIGITS.length
            ]
    ).join("");

    return `${letters}${digits}`;
};

export const normalizeRoomCode = (roomCode: string): string => {
    const cleaned = roomCode
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, ROOM_CODE_INPUT_SCAN_LIMIT);
    const letters = cleaned
        .split("")
        .filter(isRoomCodeLetter)
        .slice(0, ROOM_CODE_MAX_LETTERS)
        .join("");
    const digits = cleaned
        .split("")
        .filter(isRoomCodeDigit)
        .slice(0, ROOM_CODE_MAX_DIGITS)
        .join("");

    return `${letters}${digits}`.slice(0, ROOM_CODE_MAX_LENGTH);
};

export const isValidRoomCode = (roomCode: string): boolean => {
    const length = roomCode.length;

    if (length < ROOM_CODE_MIN_LENGTH || length > ROOM_CODE_MAX_LENGTH) {
        return false;
    }

    const letterCount = getRoomCodeLetterCount(length);
    const digitCount = getRoomCodeDigitCount(length);
    const letters = roomCode.slice(0, letterCount);
    const digits = roomCode.slice(letterCount);

    return (
        letters.length === letterCount &&
        digits.length === digitCount &&
        letters.split("").every(isRoomCodeLetter) &&
        digits.split("").every(isRoomCodeDigit)
    );
};

export const normalizePlayerName = (name: string, fallback = DEFAULT_PLAYER_NAME): string => {
    const trimmed = name.trim();

    if (!trimmed) {
        return fallback;
    }

    return trimmed.slice(0, MAX_NAME_LENGTH);
};

export const normalizeSourceText = (sourceText: string): string =>
    sourceText.replace(/\r\n?/g, "\n").trim().slice(0, MAX_SOURCE_TEXT_LENGTH);

const standardizePictionaryHintCapitalization = (stem: string): string =>
    stem.replace(/^(\s*["'([{]*)([a-z])/, (_match, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`);

export const normalizePictionaryStem = (stem: string): string =>
    standardizePictionaryHintCapitalization(
        stem.replace(/\r\n?/g, " ").replace(/\s+/g, " ").trim()
    ).slice(0, MAX_PICTIONARY_STEM_LENGTH);

const normalizePictionaryCell = (value: string): string =>
    value.replace(/\r\n?/g, " ").replace(/\s+/g, " ").trim().slice(0, MAX_PICTIONARY_CELL_LENGTH);

const NORMALIZED_PICTIONARY_STEM_MASK = "___";

const maskPictionaryStemWord = (_value: string): string => NORMALIZED_PICTIONARY_STEM_MASK;

const tokenizePictionaryStemSegments = (
    stem: string
): Array<{
    text: string;
    revealableIndex: number | null;
}> => {
    const segments: Array<{
        text: string;
        revealableIndex: number | null;
    }> = [];
    let revealableIndex = 0;
    let lastIndex = 0;

    PICTIONARY_STEM_WORD_PATTERN.lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = PICTIONARY_STEM_WORD_PATTERN.exec(stem)) !== null) {
        const start = match.index;
        const text = match[0] ?? "";

        if (start > lastIndex) {
            segments.push({
                text: stem.slice(lastIndex, start),
                revealableIndex: null
            });
        }

        segments.push({
            text,
            revealableIndex
        });
        revealableIndex += 1;
        lastIndex = start + text.length;
    }

    if (lastIndex < stem.length) {
        segments.push({
            text: stem.slice(lastIndex),
            revealableIndex: null
        });
    }

    if (segments.length === 0) {
        segments.push({
            text: stem,
            revealableIndex: null
        });
    }

    return segments;
};

const getInitialPictionaryStemRevealIndex = (
    segments: readonly {
        revealableIndex: number | null;
    }[]
): number | null => segments.find((segment) => segment.revealableIndex !== null)?.revealableIndex ?? null;

export interface PictionaryStemRevealSegment {
    text: string;
    revealableIndex: number | null;
    revealed: boolean;
}

export const clampPictionaryRevealDurationSeconds = (value: number): number =>
    Math.min(
        PICTIONARY_MAX_REVEAL_DURATION_SECONDS,
        Math.max(
            PICTIONARY_MIN_REVEAL_DURATION_SECONDS,
            Math.trunc(value / PICTIONARY_REVEAL_DURATION_STEP_SECONDS) * PICTIONARY_REVEAL_DURATION_STEP_SECONDS
        )
    );

export const buildPictionaryGuessStem = (stem: string, answerEnglish: string): string => {
    const normalizedStem = normalizePictionaryStem(stem);
    const normalizedAnswer = normalizeLooseGuess(answerEnglish);

    if (!normalizedStem || !normalizedAnswer) {
        return normalizedStem;
    }

    return tokenizePictionaryStemSegments(normalizedStem)
        .map((segment) =>
            segment.revealableIndex === null
                ? segment.text
                : normalizeLooseGuess(segment.text) === normalizedAnswer
                  ? maskPictionaryStemWord(segment.text)
                  : segment.text
        )
        .join("");
};

export const buildPictionaryStemRevealOrder = (
    guessStem: string,
    random: () => number = Math.random
): number[] => {
    const segments = tokenizePictionaryStemSegments(guessStem);
    const initialRevealIndex = getInitialPictionaryStemRevealIndex(segments);
    const order = segments.reduce<number[]>((indices, segment) => {
        if (segment.revealableIndex !== null && segment.revealableIndex !== initialRevealIndex) {
            indices.push(segment.revealableIndex);
        }

        return indices;
    }, []);

    for (let index = order.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(random() * (index + 1));
        const currentValue = order[index];
        order[index] = order[swapIndex] ?? currentValue;
        order[swapIndex] = currentValue;
    }

    return order;
};

const getPictionaryFirstStemRevealDelayMs = (revealIntervalMs: number): number =>
    Math.min(
        revealIntervalMs,
        Math.max(
            PICTIONARY_FIRST_STEM_REVEAL_MIN_DELAY_MS,
            Math.min(PICTIONARY_FIRST_STEM_REVEAL_MAX_DELAY_MS, revealIntervalMs * PICTIONARY_FIRST_STEM_REVEAL_RATIO)
        )
    );

export const getPictionaryStemRevealCount = (
    revealOrder: readonly number[],
    roundStartedAt: number | null,
    revealDurationSeconds: number,
    hintMode: PictionaryHintMode = DEFAULT_PICTIONARY_HINT_MODE,
    now = Date.now()
): number => {
    if (hintMode === "off" || revealOrder.length === 0 || roundStartedAt === null) {
        return 0;
    }

    const clampedDurationSeconds = clampPictionaryRevealDurationSeconds(revealDurationSeconds);

    if (clampedDurationSeconds <= 0) {
        return revealOrder.length;
    }

    const elapsedMs = Math.max(0, now - roundStartedAt);

    if (hintMode === "all-at-once") {
        return elapsedMs >= clampedDurationSeconds * 1000 ? revealOrder.length : 0;
    }

    const revealDurationMs = clampedDurationSeconds * 1000;

    if (revealDurationMs <= 0) {
        return revealOrder.length;
    }

    if (revealOrder.length === 1) {
        return elapsedMs >= revealDurationMs ? 1 : 0;
    }

    // The first hint word is already visible at round start. Use an early first reveal
    // for momentum, then stretch the remaining reveals so the final word lands on the configured duration.
    const openingIntervalMs = revealDurationMs / Math.max(1, revealOrder.length + 1);
    const firstRevealDelayMs = getPictionaryFirstStemRevealDelayMs(openingIntervalMs);

    if (elapsedMs < firstRevealDelayMs) {
        return 0;
    }

    const followUpIntervalMs = (revealDurationMs - firstRevealDelayMs) / Math.max(1, revealOrder.length - 1);

    if (followUpIntervalMs <= 0) {
        return revealOrder.length;
    }

    return Math.min(revealOrder.length, 1 + Math.floor((elapsedMs - firstRevealDelayMs) / followUpIntervalMs));
};

export const buildPictionaryStemRevealSegments = (
    guessStem: string,
    revealOrder: readonly number[],
    revealCount: number
): PictionaryStemRevealSegment[] => {
    const revealed = new Set(revealOrder.slice(0, Math.max(0, revealCount)));
    const segments = tokenizePictionaryStemSegments(guessStem);
    const initialRevealIndex = getInitialPictionaryStemRevealIndex(segments);

    return segments.map((segment) => {
        const isRevealed =
            segment.revealableIndex === null ||
            segment.revealableIndex === initialRevealIndex ||
            revealed.has(segment.revealableIndex);

        return {
            text: isRevealed ? segment.text : maskPictionaryStemWord(segment.text),
            revealableIndex: segment.revealableIndex,
            revealed: isRevealed
        };
    });
};

export const getPictionaryObserveEndsAt = (roundStartedAt: number | null): number | null =>
    roundStartedAt === null ? null : roundStartedAt + PICTIONARY_OBSERVE_ONLY_SECONDS * 1000;

export const isPictionaryObserveWindowActive = (
    roundStartedAt: number | null,
    now = Date.now()
): boolean => {
    const observeEndsAt = getPictionaryObserveEndsAt(roundStartedAt);

    return observeEndsAt !== null && observeEndsAt > now;
};

export const getPictionaryGuessPenaltyLives = (
    roundStartedAt: number | null,
    now = Date.now()
): number => {
    if (roundStartedAt === null) {
        return 1;
    }

    const elapsedSeconds = Math.max(0, (now - roundStartedAt) / 1000);

    if (elapsedSeconds < PICTIONARY_HIGH_RISK_GUESS_WINDOW_SECONDS) {
        return 4;
    }

    if (elapsedSeconds < PICTIONARY_MEDIUM_RISK_GUESS_WINDOW_SECONDS) {
        return 3;
    }

    if (elapsedSeconds < PICTIONARY_LOW_RISK_GUESS_WINDOW_SECONDS) {
        return 2;
    }

    return 1;
};

const normalizePictionaryAwardPointValue = (pointValue: number): number =>
    Number.isFinite(pointValue) ? Math.max(0, Math.trunc(pointValue)) : 0;

export const getPictionaryEffortPointValue = (pointValue: number): number =>
    Math.floor(normalizePictionaryAwardPointValue(pointValue) / 2);

export const getPictionaryZeroHeartSolvePointValue = (pointValue: number): number =>
    getPictionaryEffortPointValue(pointValue) + PICTIONARY_ZERO_HEART_SOLVE_BONUS;

export const getPictionarySpeedBonusThreshold = (teamCount: number): number =>
    Math.max(1, Math.ceil(Math.max(0, Math.trunc(teamCount)) * PICTIONARY_SPEED_BONUS_TEAM_SHARE));

export const getPictionarySpeedBonusPointValue = (solveRank: number): number => {
    const normalizedRank = Number.isFinite(solveRank) ? Math.trunc(solveRank) : 0;
    if (normalizedRank <= 0) {
        return 0;
    }

    // The caller still restricts speed awards to the fastest-third group and remaining hearts.
    return normalizedRank === 1
        ? PICTIONARY_FIRST_SOLVE_SPEED_BONUS_POINTS
        : normalizedRank === 2
          ? PICTIONARY_SECOND_SOLVE_SPEED_BONUS_POINTS
          : PICTIONARY_SPEED_BONUS_POINTS;
};

const normalizePictionaryAwardPoints = (points: number): number =>
    Number.isFinite(points) ? Math.max(0, Math.trunc(points)) : 0;

const formatPictionaryAwardPhrase = (points: number, singularLabel: string, pluralLabel: string): string =>
    `+${points} ${points === 1 ? singularLabel : pluralLabel}`;

export const normalizePictionaryAwardView = (award: PictionaryAwardView): PictionaryAwardView => ({
    scoreDelta: normalizePictionaryAwardPoints(award.scoreDelta),
    casePoints: normalizePictionaryAwardPoints(award.casePoints),
    speedBonusPoints: normalizePictionaryAwardPoints(award.speedBonusPoints),
    breakthroughBonusPoints: normalizePictionaryAwardPoints(award.breakthroughBonusPoints),
    effortPoints: normalizePictionaryAwardPoints(award.effortPoints),
    zeroHeartSolvePoints: normalizePictionaryAwardPoints(award.zeroHeartSolvePoints)
});

export const buildPictionarySolveAwardFeedback = (award: PictionaryAwardView): string => {
    const normalizedAward = normalizePictionaryAwardView(award);

    if (normalizedAward.scoreDelta <= 0) {
        return "Case solved!";
    }

    const parts = [
        normalizedAward.casePoints > 0
            ? { label: "case", points: normalizedAward.casePoints, single: "case point", plural: "case points" }
            : null,
        normalizedAward.zeroHeartSolvePoints > 0
            ? {
                  label: "never-quit",
                  points: normalizedAward.zeroHeartSolvePoints,
                  single: "never-quit point",
                  plural: "never-quit points"
              }
            : null,
        normalizedAward.speedBonusPoints > 0
            ? {
                  label: "speed",
                  points: normalizedAward.speedBonusPoints,
                  single: "speed bonus",
                  plural: "speed bonus"
              }
            : null,
        normalizedAward.breakthroughBonusPoints > 0
            ? {
                  label: "breakthrough",
                  points: normalizedAward.breakthroughBonusPoints,
                  single: "breakthrough bonus",
                  plural: "breakthrough bonus"
              }
            : null
    ].filter(
        (part): part is { label: string; points: number; single: string; plural: string } => part !== null
    );

    if (parts.length === 1) {
        const part = parts[0]!;
        return `Case solved! ${formatPictionaryAwardPhrase(part.points, part.single, part.plural)}.`;
    }

    return `Case solved! +${normalizedAward.scoreDelta} total: ${parts
        .map((part) => `${part.label} +${part.points}`)
        .join(", ")}.`;
};

export const buildPictionaryEffortAwardFeedback = (points: number): string =>
    `Casework ${formatPictionaryAwardPhrase(
        normalizePictionaryAwardPoints(points),
        "effort point",
        "effort points"
    )}.`;

export interface PictionaryLuckyBreakOptions {
    score: number;
    earnedPoints: number;
    otherScores: readonly number[];
    /** Frozen pre-case totals: earned points gate the award but do not change its deficit. */
    scoreBasis?: "round-start";
    effortGuessCount?: number;
    randomUnit?: number;
}

const normalizePictionaryScoreValue = (score: number): number =>
    Number.isFinite(score) ? Math.max(0, Math.trunc(score)) : 0;

const normalizePictionaryLuckyBreakRoll = (randomUnit: number): number =>
    Number.isFinite(randomUnit) ? Math.min(1, Math.max(0, randomUnit)) : 1;

const normalizePictionaryEffortGuessCount = (effortGuessCount: number | undefined): number =>
    typeof effortGuessCount === "number" && Number.isFinite(effortGuessCount)
        ? Math.max(0, Math.trunc(effortGuessCount))
        : 0;

export const getPictionaryLuckyBreakScoreCap = (
    otherScores: readonly number[],
    maxPromotedRank = PICTIONARY_LUCKY_BREAK_MAX_PROMOTED_RANK
): number => {
    const normalizedMaxPromotedRank = Math.max(1, Math.trunc(maxPromotedRank));
    const protectedRankIndex = normalizedMaxPromotedRank - 2;

    if (protectedRankIndex < 0) {
        return 0;
    }

    const rankedOtherScores = otherScores
        .map((score) => normalizePictionaryScoreValue(score))
        .sort((left, right) => right - left);

    const protectedScore = rankedOtherScores[protectedRankIndex];

    if (typeof protectedScore !== "number") {
        return 0;
    }

    return Math.max(0, protectedScore - PICTIONARY_LUCKY_BREAK_THIRD_PLACE_BUFFER_POINTS);
};

export const getPictionaryLuckyBreakChance = (deficitPoints: number, effortGuessCount = 0): number => {
    const normalizedDeficit = normalizePictionaryScoreValue(deficitPoints);

    if (normalizedDeficit < PICTIONARY_LUCKY_BREAK_MIN_DEFICIT_POINTS) {
        return 0;
    }

    const baseChance = Math.min(
        PICTIONARY_LUCKY_BREAK_MAX_CHANCE,
        PICTIONARY_LUCKY_BREAK_BASE_CHANCE +
            normalizedDeficit * PICTIONARY_LUCKY_BREAK_CHANCE_PER_DEFICIT_POINT
    );
    const effortChanceBoost =
        normalizePictionaryEffortGuessCount(effortGuessCount) *
        PICTIONARY_LUCKY_BREAK_EFFORT_CHANCE_PER_GUESS;

    return Math.min(PICTIONARY_LUCKY_BREAK_EFFORT_MAX_CHANCE, baseChance + effortChanceBoost);
};

export const hasPictionaryLuckyBreakChance = ({
    score,
    earnedPoints,
    otherScores,
    effortGuessCount,
    scoreBasis
}: PictionaryLuckyBreakOptions): boolean => {
    const normalizedScore = normalizePictionaryScoreValue(score);
    const normalizedEarnedPoints = normalizePictionaryScoreValue(earnedPoints);

    if (normalizedEarnedPoints <= 0) {
        return false;
    }

    const scoreAfterEarnedPoints = normalizedScore + (scoreBasis === "round-start" ? 0 : normalizedEarnedPoints);
    const scoreCap = getPictionaryLuckyBreakScoreCap(otherScores);
    const eligibilityScoreCap = getPictionaryLuckyBreakScoreCap(
        otherScores,
        PICTIONARY_LUCKY_BREAK_MIN_ELIGIBLE_RANK
    );

    if (scoreAfterEarnedPoints > eligibilityScoreCap) {
        return false;
    }

    const deficitPoints = scoreCap - scoreAfterEarnedPoints;

    return getPictionaryLuckyBreakChance(deficitPoints, effortGuessCount) > 0;
};

export const getPictionaryLuckyBreakBonusPoints = ({
    score,
    earnedPoints,
    otherScores,
    effortGuessCount,
    randomUnit = Math.random(),
    scoreBasis
}: PictionaryLuckyBreakOptions): number => {
    const normalizedScore = normalizePictionaryScoreValue(score);
    const normalizedEarnedPoints = normalizePictionaryScoreValue(earnedPoints);

    if (normalizedEarnedPoints <= 0) {
        return 0;
    }

    if (!hasPictionaryLuckyBreakChance({ score, earnedPoints, otherScores, effortGuessCount, scoreBasis })) {
        return 0;
    }

    const scoreAfterEarnedPoints = normalizedScore + (scoreBasis === "round-start" ? 0 : normalizedEarnedPoints);
    const scoreCap = getPictionaryLuckyBreakScoreCap(otherScores);
    const deficitPoints = scoreCap - scoreAfterEarnedPoints;
    const chance = getPictionaryLuckyBreakChance(deficitPoints, effortGuessCount);

    if (chance <= 0) {
        return 0;
    }

    const roll = normalizePictionaryLuckyBreakRoll(randomUnit);

    if (roll >= chance) {
        return 0;
    }

    const rollStrength = 1 - roll / chance;
    const targetShare =
        PICTIONARY_LUCKY_BREAK_MIN_TARGET_SHARE +
        (1 - PICTIONARY_LUCKY_BREAK_MIN_TARGET_SHARE) * rollStrength;

    return Math.min(deficitPoints, Math.max(1, Math.ceil(deficitPoints * targetShare)));
};

export const normalizeWordToken = (token: string): string => {
    const lowered = token
        .trim()
        .toLowerCase()
        .replace(/[\u2018\u2019`]/g, "'")
        .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, "-");
    const stripped = lowered.replace(WORD_EDGE_TRIM_PATTERN, "");
    const trimmedInternal = stripped.replace(/^['-]+|['-]+$/g, "");
    return trimmedInternal || stripped || lowered;
};

export const normalizeLooseGuess = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .replace(/[\u2018\u2019`]/g, "'")
        .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, "-")
        .normalize("NFKC")
        .replace(NON_WORD_GLOBAL_PATTERN, "");

type VaultBreakerAnswerIdentity = Pick<VaultBreakerEntryDefinition, "normalizedAnswer">;

export const hasSameVaultBreakerAnswer = (
    left: VaultBreakerAnswerIdentity | null | undefined,
    right: VaultBreakerAnswerIdentity | null | undefined
): boolean => {
    if (!left || !right || !left.normalizedAnswer) {
        return false;
    }

    return left.normalizedAnswer === right.normalizedAnswer;
};

// Source rows stay distinct vaults, but repeated answer text is one scanner choice identity.
export const getUniqueVaultBreakerAnswerEntries = <T extends VaultBreakerAnswerIdentity>(
    entries: readonly T[]
): T[] => {
    const seenAnswers = new Set<string>();

    return entries.filter((entry) => {
        if (!entry.normalizedAnswer || seenAnswers.has(entry.normalizedAnswer)) {
            return false;
        }

        seenAnswers.add(entry.normalizedAnswer);
        return true;
    });
};

const normalizeVaultBreakerCell = (value: string, maxLength: number): string =>
    value.replace(/\r\n?/g, " ").replace(/\s+/g, " ").trim().slice(0, maxLength);

type VaultBreakerTailSeparatorKind = "parenthesis" | "semicolon" | "dash" | "pipe" | "tab" | "arrow";

const VAULT_BREAKER_TAIL_SEPARATOR_PATTERN =
    /\t+|\s*(?:->|=>|\u2192|\u21d2)\s*|\s*\|\s*|\s*;\s*|\s+[-\u2013\u2014]\s+/g;
const VAULT_BREAKER_ARROW_SEPARATOR_PATTERN = /->|=>|\u2192|\u21d2/g;
const VAULT_BREAKER_BLANK_MARKER_PATTERN = /_{2,}|\[\s*\]|\(\s*\)|\.\.\.|[\u2026]/;
const VAULT_BREAKER_SENTENCE_END_PATTERN = /[.?!]["')\]]*$/;
const VAULT_BREAKER_HANGUL_PATTERN = /[\u1100-\u11ff\u3130-\u318f\uac00-\ud7af]/;
const VAULT_BREAKER_ANSWER_SENTENCE_PUNCTUATION_PATTERN = /[.?!]/;

const classifyVaultBreakerTailSeparator = (separator: string): VaultBreakerTailSeparatorKind => {
    if (separator.includes("\t")) {
        return "tab";
    }

    if (/->|=>|\u2192|\u21d2/.test(separator)) {
        return "arrow";
    }

    if (separator.includes("|")) {
        return "pipe";
    }

    if (separator.includes(";")) {
        return "semicolon";
    }

    return "dash";
};

const getVaultBreakerTailSeparatorBias = (kind: VaultBreakerTailSeparatorKind): number => {
    switch (kind) {
        case "parenthesis":
            return 8;
        case "arrow":
        case "pipe":
        case "tab":
            return 4;
        case "semicolon":
            return 3;
        case "dash":
        default:
            return 2;
    }
};

const countVaultBreakerWords = (value: string): number =>
    value
        .trim()
        .split(/\s+/)
        .filter((part) => normalizeLooseGuess(part)).length;

const hasLikelyVaultBreakerPromptCue = (prompt: string): boolean => {
    const trimmed = prompt.trim();

    return (
        VAULT_BREAKER_BLANK_MARKER_PATTERN.test(trimmed) ||
        VAULT_BREAKER_SENTENCE_END_PATTERN.test(trimmed) ||
        countVaultBreakerWords(trimmed) >= 4
    );
};

const splitVaultBreakerFlexibleCells = (
    value: string
): { first: string; rest: string; separatorKind: VaultBreakerTailSeparatorKind | null } => {
    VAULT_BREAKER_TAIL_SEPARATOR_PATTERN.lastIndex = 0;
    const match = VAULT_BREAKER_TAIL_SEPARATOR_PATTERN.exec(value);

    if (!match) {
        return {
            first: value,
            rest: "",
            separatorKind: null
        };
    }

    return {
        first: value.slice(0, match.index),
        rest: value.slice(match.index + match[0].length),
        separatorKind: classifyVaultBreakerTailSeparator(match[0])
    };
};

const splitVaultBreakerAnswerCell = (value: string): { answer: string; korean: string } => {
    const { first, rest } = splitVaultBreakerFlexibleCells(value);

    return {
        answer: normalizeVaultBreakerCell(first, MAX_VAULT_BREAKER_ANSWER_LENGTH),
        korean: normalizeVaultBreakerCell(rest, MAX_VAULT_BREAKER_ANSWER_LENGTH)
    };
};

const getVaultBreakerArrowChainTailAnswerValue = (prompt: string): string | null => {
    let arrowCount = 0;
    let lastArrowEnd = -1;

    VAULT_BREAKER_ARROW_SEPARATOR_PATTERN.lastIndex = 0;
    for (const match of prompt.matchAll(VAULT_BREAKER_ARROW_SEPARATOR_PATTERN)) {
        arrowCount += 1;
        lastArrowEnd = (match.index ?? -1) + match[0].length;
    }

    if (arrowCount < 2 || lastArrowEnd < 0) {
        return null;
    }

    const tailAnswer = normalizeVaultBreakerCell(prompt.slice(lastArrowEnd), MAX_VAULT_BREAKER_ANSWER_LENGTH);

    if (
        !tailAnswer ||
        splitVaultBreakerFlexibleCells(tailAnswer).separatorKind !== null ||
        VAULT_BREAKER_HANGUL_PATTERN.test(tailAnswer) ||
        VAULT_BREAKER_BLANK_MARKER_PATTERN.test(tailAnswer) ||
        countVaultBreakerWords(tailAnswer) > 4 ||
        !normalizeLooseGuess(tailAnswer)
    ) {
        return null;
    }

    return tailAnswer;
};

const getVaultBreakerArrowChainTailAnswer = (
    prompt: string,
    answer: string,
    korean: string,
    separatorKind: VaultBreakerTailSeparatorKind
): { answer: string; korean: string } | null => {
    if (
        separatorKind !== "semicolon" ||
        korean ||
        !VAULT_BREAKER_HANGUL_PATTERN.test(answer) ||
        !VAULT_BREAKER_BLANK_MARKER_PATTERN.test(prompt)
    ) {
        return null;
    }

    const tailAnswer = getVaultBreakerArrowChainTailAnswerValue(prompt);

    if (!tailAnswer) {
        return null;
    }

    return {
        answer: tailAnswer,
        korean: answer
    };
};

const stripVaultBreakerValuePrefix = (line: string): { value: number | null; prompt: string } => {
    const match = line.match(/^\s*(\d{1,5})\s*(?:[-\u2013\u2014.)]|:)\s*(.+)$/);

    if (!match) {
        return {
            value: null,
            prompt: line
        };
    }

    const value = Number.parseInt(match[1] ?? "", 10);

    return {
        value: Number.isFinite(value) ? value : null,
        prompt: match[2] ?? ""
    };
};

const parseVaultBreakerQuestionLine = (
    line: string
): { prompt: string; answer: string; korean: string; value: number | null } | null => {
    const trimmed = line.trim();
    const { value, prompt: promptAndAnswer } = stripVaultBreakerValuePrefix(trimmed);
    const candidates: Array<{
        prompt: string;
        answerCell: string;
        separatorKind: VaultBreakerTailSeparatorKind;
    }> = [];
    const answerMatch = promptAndAnswer.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
    const arrowChainTailAnswer = VAULT_BREAKER_BLANK_MARKER_PATTERN.test(promptAndAnswer)
        ? getVaultBreakerArrowChainTailAnswerValue(promptAndAnswer)
        : null;

    if (arrowChainTailAnswer) {
        candidates.push({
            prompt: promptAndAnswer,
            answerCell: arrowChainTailAnswer,
            separatorKind: "arrow"
        });
    }

    if (answerMatch) {
        candidates.push({
            prompt: answerMatch[1] ?? "",
            answerCell: answerMatch[2] ?? "",
            separatorKind: "parenthesis"
        });
    }

    VAULT_BREAKER_TAIL_SEPARATOR_PATTERN.lastIndex = 0;
    for (const match of promptAndAnswer.matchAll(VAULT_BREAKER_TAIL_SEPARATOR_PATTERN)) {
        const separator = match[0] ?? "";
        const separatorStart = match.index ?? -1;

        if (separatorStart < 0) {
            continue;
        }

        candidates.push({
            prompt: promptAndAnswer.slice(0, separatorStart),
            answerCell: promptAndAnswer.slice(separatorStart + separator.length),
            separatorKind: classifyVaultBreakerTailSeparator(separator)
        });
    }

    let best:
        | {
              prompt: string;
              answer: string;
              korean: string;
              score: number;
          }
        | null = null;

    for (const candidate of candidates) {
        const normalizedPrompt = normalizeVaultBreakerCell(candidate.prompt, MAX_VAULT_BREAKER_PROMPT_LENGTH);
        const answerCellParts = splitVaultBreakerAnswerCell(candidate.answerCell);
        let normalizedAnswer = answerCellParts.answer;
        let normalizedKorean = answerCellParts.korean;
        const arrowChainTailAnswer = getVaultBreakerArrowChainTailAnswer(
            normalizedPrompt,
            normalizedAnswer,
            normalizedKorean,
            candidate.separatorKind
        );

        if (arrowChainTailAnswer) {
            normalizedAnswer = arrowChainTailAnswer.answer;
            normalizedKorean = arrowChainTailAnswer.korean;
        }

        const answerWordCount = countVaultBreakerWords(normalizedAnswer);
        const promptWordCount = countVaultBreakerWords(normalizedPrompt);
        const valuePrefixSignalsQuestion =
            value !== null && (candidate.separatorKind !== "dash" || Boolean(normalizedKorean));

        if (!normalizedPrompt || !normalizedAnswer || !normalizeLooseGuess(normalizedAnswer)) {
            continue;
        }

        if (
            candidate.separatorKind !== "parenthesis" &&
            !valuePrefixSignalsQuestion &&
            !hasLikelyVaultBreakerPromptCue(normalizedPrompt)
        ) {
            continue;
        }

        if (
            VAULT_BREAKER_BLANK_MARKER_PATTERN.test(normalizedAnswer) ||
            (answerWordCount > 3 &&
                VAULT_BREAKER_ANSWER_SENTENCE_PUNCTUATION_PATTERN.test(normalizedAnswer))
        ) {
            continue;
        }

        if (answerWordCount > 8) {
            continue;
        }

        const hasTranslation = Boolean(normalizedKorean);
        const answerLooksKorean = VAULT_BREAKER_HANGUL_PATTERN.test(normalizedAnswer);
        const translationLooksKorean = VAULT_BREAKER_HANGUL_PATTERN.test(normalizedKorean);
        const promptContainsSeparator = splitVaultBreakerFlexibleCells(normalizedPrompt).separatorKind !== null;
        let score = getVaultBreakerTailSeparatorBias(candidate.separatorKind);

        if (VAULT_BREAKER_BLANK_MARKER_PATTERN.test(normalizedPrompt)) {
            score += 5;
        }

        if (VAULT_BREAKER_SENTENCE_END_PATTERN.test(normalizedPrompt)) {
            score += 3;
        }

        if (promptWordCount >= 4) {
            score += 2;
        }

        if (answerWordCount <= 4) {
            score += 2;
        }

        if (hasTranslation) {
            score += 2;
        }

        if (translationLooksKorean) {
            score += 2;
        }

        if (arrowChainTailAnswer) {
            score += 4;
        }

        if (answerLooksKorean && !hasTranslation) {
            score -= 3;
        }

        if (promptContainsSeparator) {
            score -= 2;
        }

        if (!best || score > best.score) {
            best = {
                prompt: normalizedPrompt,
                answer: normalizedAnswer,
                korean: normalizedKorean,
                score
            };
        }
    }

    return best
        ? {
              prompt: best.prompt,
              answer: best.answer,
              korean: best.korean,
              value
          }
        : null;
};

const buildVaultBreakerEntryId = (categoryIndex: number, valueIndex: number): string =>
    `vault-${categoryIndex + 1}-${valueIndex + 1}`;

export const getVaultBreakerSourceGuideLines = (sourceText: string): VaultBreakerSourceGuideLine[] => {
    const lines = sourceText
        .replace(/\r\n?/g, "\n")
        .slice(0, MAX_SOURCE_TEXT_LENGTH)
        .split("\n");
    const guideLines: VaultBreakerSourceGuideLine[] = [];
    const categoryGuideLines = new Map<number, Extract<VaultBreakerSourceGuideLine, { kind: "category" }>>();
    const entryCountsByCategory = new Map<number, number>();
    let currentCategory = "Vaults";
    let currentCategoryIndex = -1;
    let currentValueIndex = 0;

    lines.forEach((line, lineIndex) => {
        const trimmed = line.trim();

        if (!trimmed) {
            guideLines.push({
                kind: "empty",
                lineIndex
            });
            return;
        }

        const parsedQuestion = parseVaultBreakerQuestionLine(trimmed);

        if (!parsedQuestion) {
            currentCategory = normalizeVaultBreakerCell(trimmed, MAX_VAULT_BREAKER_CATEGORY_LENGTH);
            currentCategoryIndex += 1;
            currentValueIndex = 0;
            const guideLine: Extract<VaultBreakerSourceGuideLine, { kind: "category" }> = {
                kind: "category",
                lineIndex,
                category: currentCategory,
                categoryIndex: currentCategoryIndex,
                entryCount: 0
            };

            categoryGuideLines.set(currentCategoryIndex, guideLine);
            guideLines.push(guideLine);
            return;
        }

        if (currentCategoryIndex < 0) {
            currentCategoryIndex = 0;
        }

        const value =
            parsedQuestion.value ??
            VAULT_BREAKER_DEFAULT_VALUES[currentValueIndex] ??
            (currentValueIndex + 1) * 100;

        guideLines.push({
            kind: "vault",
            lineIndex,
            category: currentCategory,
            categoryIndex: currentCategoryIndex,
            value,
            valueIndex: currentValueIndex,
            answer: parsedQuestion.answer,
            korean: parsedQuestion.korean
        });
        entryCountsByCategory.set(currentCategoryIndex, (entryCountsByCategory.get(currentCategoryIndex) ?? 0) + 1);
        currentValueIndex += 1;
    });

    categoryGuideLines.forEach((guideLine, categoryIndex) => {
        guideLine.entryCount = entryCountsByCategory.get(categoryIndex) ?? 0;
    });

    return guideLines;
};

export const parseVaultBreakerSource = (sourceText: string): VaultBreakerEntryDefinition[] => {
    const lines = normalizeSourceText(sourceText).split("\n");
    const entries: VaultBreakerEntryDefinition[] = [];
    let currentCategory = "Vaults";
    let currentCategoryIndex = -1;
    let currentValueIndex = 0;

    for (const line of lines) {
        const trimmed = line.trim();

        if (!trimmed) {
            continue;
        }

        const parsedQuestion = parseVaultBreakerQuestionLine(trimmed);

        if (!parsedQuestion) {
            currentCategory = normalizeVaultBreakerCell(trimmed, MAX_VAULT_BREAKER_CATEGORY_LENGTH);
            currentCategoryIndex += 1;
            currentValueIndex = 0;
            continue;
        }

        if (currentCategoryIndex < 0) {
            currentCategoryIndex = 0;
        }

        const value =
            parsedQuestion.value ??
            VAULT_BREAKER_DEFAULT_VALUES[currentValueIndex] ??
            (currentValueIndex + 1) * 100;
        const normalizedAnswer = normalizeLooseGuess(parsedQuestion.answer);

        entries.push({
            id: buildVaultBreakerEntryId(currentCategoryIndex, currentValueIndex),
            category: currentCategory,
            categoryIndex: currentCategoryIndex,
            value,
            valueIndex: currentValueIndex,
            prompt: parsedQuestion.prompt,
            answer: parsedQuestion.answer,
            korean: parsedQuestion.korean,
            normalizedAnswer
        });
        currentValueIndex += 1;
    }

    return entries;
};

export const getVaultBreakerCategoryCount = (entries: readonly VaultBreakerEntryDefinition[]): number =>
    new Set(entries.map((entry) => entry.categoryIndex)).size;

export const getVaultBreakerMaxValueIndex = (entries: readonly VaultBreakerEntryDefinition[]): number =>
    entries.reduce((maximum, entry) => Math.max(maximum, entry.valueIndex), -1);

export const getVaultBreakerValueUnlockThreshold = (valueIndex: number): number => {
    const normalizedValueIndex =
        typeof valueIndex === "number" && Number.isFinite(valueIndex) ? Math.max(0, Math.trunc(valueIndex)) : 0;

    if (normalizedValueIndex <= 0) {
        return 0;
    }

    const configuredThreshold = VAULT_BREAKER_VALUE_UNLOCK_THRESHOLDS[normalizedValueIndex - 1];

    if (typeof configuredThreshold === "number") {
        return configuredThreshold;
    }

    const finalConfiguredValueIndex = VAULT_BREAKER_VALUE_UNLOCK_THRESHOLDS.length;
    const finalConfiguredThreshold = VAULT_BREAKER_VALUE_UNLOCK_THRESHOLDS[finalConfiguredValueIndex - 1] ?? 0;

    return finalConfiguredThreshold + (normalizedValueIndex - finalConfiguredValueIndex) * 3;
};

export const getVaultBreakerUnlockedValueIndex = (
    clearedCount: number,
    categoryCount: number,
    maxValueIndex: number,
    manualUnlockedValueIndex = 0
): number => {
    const normalizedClearedCount =
        typeof clearedCount === "number" && Number.isFinite(clearedCount) ? Math.max(0, Math.trunc(clearedCount)) : 0;
    const normalizedMaxValueIndex =
        typeof maxValueIndex === "number" && Number.isFinite(maxValueIndex) ? Math.max(0, Math.trunc(maxValueIndex)) : 0;
    const normalizedManualUnlockedValueIndex =
        typeof manualUnlockedValueIndex === "number" && Number.isFinite(manualUnlockedValueIndex)
            ? Math.min(normalizedMaxValueIndex, Math.max(0, Math.trunc(manualUnlockedValueIndex)))
            : 0;
    let unlockedValueIndex = normalizedManualUnlockedValueIndex;

    for (let valueIndex = 1; valueIndex <= normalizedMaxValueIndex; valueIndex += 1) {
        const threshold = getVaultBreakerValueUnlockThreshold(valueIndex);

        if (normalizedClearedCount >= threshold) {
            unlockedValueIndex = Math.max(unlockedValueIndex, valueIndex);
        }
    }

    return unlockedValueIndex;
};

export const isVaultBreakerEntryUnlocked = (
    entry: Pick<VaultBreakerEntryDefinition, "valueIndex">,
    clearedCount: number,
    categoryCount: number,
    maxValueIndex: number,
    manualUnlockedValueIndex = 0
): boolean =>
    entry.valueIndex <=
    getVaultBreakerUnlockedValueIndex(clearedCount, categoryCount, maxValueIndex, manualUnlockedValueIndex);

export const clampVaultBreakerCrackNumber = (value: unknown): VaultBreakerCrackNumber => {
    const integerValue = typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : 1;

    return Math.min(3, Math.max(1, integerValue)) as VaultBreakerCrackNumber;
};

export const clampVaultBreakerCrackDurationSeconds = (value: unknown): number => {
    const integerValue =
        typeof value === "number" && Number.isFinite(value)
            ? Math.trunc(value)
            : VAULT_BREAKER_DEFAULT_CRACK_DURATION_SECONDS;

    return Math.min(
        VAULT_BREAKER_MAX_CRACK_DURATION_SECONDS,
        Math.max(VAULT_BREAKER_MIN_CRACK_DURATION_SECONDS, integerValue)
    );
};

export const getVaultBreakerCrackDurationSeconds = (
    crackNumber: VaultBreakerCrackNumber,
    durationSeconds?: number | null,
    additionalSeconds = 0
): number => {
    const crackIndex = clampVaultBreakerCrackNumber(crackNumber) - 1;
    const baseDurationSeconds = clampVaultBreakerCrackDurationSeconds(
        durationSeconds ?? VAULT_BREAKER_DEFAULT_CRACK_DURATION_SECONDS
    );
    const firstCrackDurationSeconds = VAULT_BREAKER_CRACK_DURATIONS_SECONDS[0] ?? VAULT_BREAKER_DEFAULT_CRACK_DURATION_SECONDS;
    const targetDurationSeconds = VAULT_BREAKER_CRACK_DURATIONS_SECONDS[crackIndex] ?? firstCrackDurationSeconds;
    const stagedReductionSeconds = firstCrackDurationSeconds - targetDurationSeconds;

    const normalizedAdditionalSeconds = Number.isFinite(additionalSeconds)
        ? Math.max(0, Math.trunc(additionalSeconds))
        : 0;

    return Math.max(
        VAULT_BREAKER_MIN_CRACK_DURATION_SECONDS,
        baseDurationSeconds - stagedReductionSeconds + normalizedAdditionalSeconds
    );
};

export const getVaultBreakerFinalCrackDurationSeconds = (_durationSeconds?: number | null): number =>
    VAULT_BREAKER_FINAL_CRACK_DURATION_SECONDS;

export const getVaultBreakerFinalPrize = (values: readonly number[]): number =>
    Math.max(
        VAULT_BREAKER_FINAL_MIN_PRIZE,
        values.reduce(
            (maximum, value) =>
                Math.max(
                    maximum,
                    typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0
                ),
            0
        ) + VAULT_BREAKER_FINAL_PRIZE_MARGIN
    );

export const getVaultBreakerFinalEligibleValue = (
    entries: ReadonlyArray<{ value: number; cleared: boolean }>
): number | null => {
    const remainingValues = entries
        .filter((entry) => !entry.cleared)
        .map((entry) =>
            typeof entry.value === "number" && Number.isFinite(entry.value) ? Math.max(0, Math.trunc(entry.value)) : 0
        );

    return remainingValues.length > 0 ? Math.max(...remainingValues) : null;
};

export const getVaultBreakerChoiceCount = (crackNumber: VaultBreakerCrackNumber, availableCount: number): number => {
    const targetCount = VAULT_BREAKER_CHOICE_COUNTS[crackNumber - 1] ?? VAULT_BREAKER_CHOICE_COUNTS[0];
    const normalizedAvailableCount = Math.max(1, Math.trunc(availableCount));

    return Math.min(
        normalizedAvailableCount,
        Math.max(VAULT_BREAKER_MIN_CHOICE_COUNT, targetCount)
    );
};

export const getVaultBreakerArmingEndsAt = (roundStartedAt: number | null): number | null =>
    roundStartedAt === null ? null : roundStartedAt + VAULT_BREAKER_ARMING_SECONDS * 1000;

export const getVaultBreakerCrackEndsAt = (
    crackStartedAt: number | null,
    crackNumber: VaultBreakerCrackNumber | null,
    durationSeconds?: number | null,
    additionalSeconds = 0
): number | null =>
    crackStartedAt === null || crackNumber === null
        ? null
        : crackStartedAt + getVaultBreakerCrackDurationSeconds(crackNumber, durationSeconds, additionalSeconds) * 1000;

export const getVaultBreakerCrackAutoSubmitAt = (
    crackStartedAt: number | null,
    crackNumber: VaultBreakerCrackNumber | null,
    durationSeconds?: number | null,
    additionalSeconds = 0
): number | null => {
    const crackEndsAt = getVaultBreakerCrackEndsAt(crackStartedAt, crackNumber, durationSeconds, additionalSeconds);

    return crackEndsAt === null ? null : crackEndsAt + VAULT_BREAKER_CRACK_ZERO_COYOTE_MS;
};

export const getVaultBreakerCrackResolveAt = (
    crackStartedAt: number | null,
    crackNumber: VaultBreakerCrackNumber | null,
    durationSeconds?: number | null,
    additionalSeconds = 0
): number | null => {
    const autoSubmitAt = getVaultBreakerCrackAutoSubmitAt(
        crackStartedAt,
        crackNumber,
        durationSeconds,
        additionalSeconds
    );

    return autoSubmitAt === null ? null : autoSubmitAt + VAULT_BREAKER_CRACK_GRACE_MS;
};

export const getVaultBreakerNextCrackStartsAt = (resolvedAt: number): number =>
    resolvedAt + VAULT_BREAKER_CRACK_GRACE_MS;

export const getVaultBreakerEffortPointValue = (value: number): number =>
    Math.max(1, Math.floor(Math.max(0, Math.trunc(value)) * VAULT_BREAKER_EFFORT_POINT_SHARE));

export const getVaultBreakerResolvedCrackOutcome = ({
    crackNumber,
    solvedCount,
    participantCount,
    crackLimit = 3
}: {
    crackNumber: VaultBreakerCrackNumber;
    solvedCount: number;
    participantCount: number;
    crackLimit?: number;
}): {
    allSolved: boolean;
    anySolved: boolean;
    roundComplete: boolean;
    entryConsumed: boolean;
} => {
    const normalizedSolvedCount = Math.max(0, Math.trunc(solvedCount));
    const normalizedParticipantCount = Math.max(0, Math.trunc(participantCount));
    const allSolved =
        normalizedParticipantCount > 0 && normalizedSolvedCount >= normalizedParticipantCount;
    const anySolved = normalizedSolvedCount > 0;
    const normalizedCrackLimit = Math.max(1, Math.trunc(crackLimit));
    const roundComplete = crackNumber >= normalizedCrackLimit || allSolved;

    return {
        allSolved,
        anySolved,
        roundComplete,
        entryConsumed: roundComplete
    };
};

export const getVaultBreakerEarlyBonusPoints = (
    value: number,
    crackNumber: VaultBreakerCrackNumber,
    selectedAt: number | null,
    crackStartedAt: number | null,
    durationSeconds?: number | null,
    additionalSeconds = 0
): number => {
    if (selectedAt === null || crackStartedAt === null) {
        return 0;
    }

    const baseValue = Math.max(0, Math.trunc(value));
    const bonusShare = VAULT_BREAKER_CRACK_BONUS_SHARES[crackNumber - 1] ?? 0;

    if (baseValue <= 0 || bonusShare <= 0) {
        return 0;
    }

    const durationMs = getVaultBreakerCrackDurationSeconds(crackNumber, durationSeconds, additionalSeconds) * 1000;
    const elapsedMs = Math.max(0, selectedAt - crackStartedAt);
    const remainingRatio = Math.max(0, 1 - elapsedMs / Math.max(1, durationMs));

    return Math.max(0, Math.ceil(baseValue * bonusShare * remainingRatio));
};

const normalizeVaultBreakerScoreValue = (score: number): number =>
    Number.isFinite(score) ? Math.max(0, Math.trunc(score)) : 0;

export const getVaultBreakerComebackScoreCap = (
    otherScores: readonly number[],
    maxPromotedRank = VAULT_BREAKER_COMEBACK_MAX_PROMOTED_RANK
): number => {
    const normalizedMaxPromotedRank = Math.max(1, Math.trunc(maxPromotedRank));
    const protectedRankIndex = normalizedMaxPromotedRank - 2;

    if (protectedRankIndex < 0) {
        return 0;
    }

    const rankedOtherScores = otherScores
        .map((score) => normalizeVaultBreakerScoreValue(score))
        .sort((left, right) => right - left);
    const protectedScore = rankedOtherScores[protectedRankIndex];

    if (typeof protectedScore !== "number") {
        return 0;
    }

    return Math.max(0, protectedScore - VAULT_BREAKER_COMEBACK_SCORE_BUFFER_POINTS);
};

export const getVaultBreakerComebackCompetitionScale = (activeCompetitorCount?: number | null): number => {
    if (typeof activeCompetitorCount !== "number" || !Number.isFinite(activeCompetitorCount)) {
        return 1;
    }

    const normalizedCount = Math.max(0, Math.trunc(activeCompetitorCount));

    if (normalizedCount <= 1) {
        return 0;
    }

    return normalizedCount === 2 ? VAULT_BREAKER_COMEBACK_TWO_TEAM_SCALE : 1;
};

export const getVaultBreakerComebackBonusPoints = ({
    score,
    earnedPoints,
    earlyBonusPoints,
    otherScores,
    activeCompetitorCount
}: {
    score: number;
    earnedPoints: number;
    earlyBonusPoints: number;
    otherScores: readonly number[];
    activeCompetitorCount?: number | null;
}): number => {
    const normalizedScore = normalizeVaultBreakerScoreValue(score);
    const normalizedEarnedPoints = normalizeVaultBreakerScoreValue(earnedPoints);
    const normalizedEarlyBonus = normalizeVaultBreakerScoreValue(earlyBonusPoints);
    const competitionScale = getVaultBreakerComebackCompetitionScale(activeCompetitorCount);

    if (normalizedEarnedPoints <= 0 || normalizedEarlyBonus <= 0 || competitionScale <= 0) {
        return 0;
    }

    const scoreCap = getVaultBreakerComebackScoreCap(otherScores);
    const deficitPoints = scoreCap - (normalizedScore + normalizedEarnedPoints);

    if (deficitPoints <= 0) {
        return 0;
    }

    const speedShare = normalizedEarlyBonus / Math.max(1, normalizedEarnedPoints);
    const deficitShare = Math.min(
        VAULT_BREAKER_COMEBACK_MAX_DEFICIT_SHARE,
        Math.max(
            VAULT_BREAKER_COMEBACK_MIN_DEFICIT_SHARE,
            speedShare * VAULT_BREAKER_COMEBACK_DEFICIT_SPEED_SHARE
        )
    );
    const fastLockBonus = normalizedEarlyBonus * VAULT_BREAKER_COMEBACK_FAST_LOCK_MULTIPLIER;
    const deficitBoost = deficitPoints * deficitShare;
    const unscaledBonus = Math.min(deficitPoints, Math.max(1, Math.ceil(fastLockBonus + deficitBoost)));

    return Math.min(deficitPoints, Math.ceil(unscaledBonus * competitionScale));
};

export const clampVaultBreakerPointMultiplier = (value: unknown): number => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return 1;
    }

    return value >= VAULT_BREAKER_SURGE_MULTIPLIER ? VAULT_BREAKER_SURGE_MULTIPLIER : 1;
};

export const getVaultBreakerSurgeChance = ({
    score,
    otherScores,
    completedRounds,
    previousRoundWasSurge = false
}: {
    score: number;
    otherScores: readonly number[];
    completedRounds: number;
    previousRoundWasSurge?: boolean;
}): number => {
    if (!Number.isFinite(completedRounds) || completedRounds < VAULT_BREAKER_SURGE_WARMUP_ROUNDS ||
        otherScores.length === 0 || previousRoundWasSurge) {
        return 0;
    }

    const normalizedScore = normalizeVaultBreakerScoreValue(score);
    const highestOtherScore = otherScores.reduce(
        (highest, candidate) => Math.max(highest, normalizeVaultBreakerScoreValue(candidate)),
        0
    );
    const deficitSteps = Math.floor(
        Math.max(0, highestOtherScore - normalizedScore) / VAULT_BREAKER_SURGE_DEFICIT_STEP_POINTS
    );

    return Math.min(
        VAULT_BREAKER_SURGE_MAX_CHANCE,
        VAULT_BREAKER_SURGE_BASE_CHANCE + deficitSteps * VAULT_BREAKER_SURGE_CHANCE_PER_DEFICIT_STEP
    );
};

export const getVaultBreakerGrowingStashVaultChance = ({
    entryValueIndex,
    clearedCount,
    categoryCount,
    maxValueIndex,
    manualUnlockedValueIndex = 0,
    currentBonusPoints = 0
}: {
    entryValueIndex: number;
    clearedCount: number;
    categoryCount: number;
    maxValueIndex: number;
    manualUnlockedValueIndex?: number;
    currentBonusPoints?: number;
}): number => {
    const normalizedEntryValueIndex =
        typeof entryValueIndex === "number" && Number.isFinite(entryValueIndex) ? Math.max(0, Math.trunc(entryValueIndex)) : 0;
    const normalizedClearedCount =
        typeof clearedCount === "number" && Number.isFinite(clearedCount) ? Math.max(0, Math.trunc(clearedCount)) : 0;
    const normalizedCategoryCount =
        typeof categoryCount === "number" && Number.isFinite(categoryCount) ? Math.max(1, Math.trunc(categoryCount)) : 1;
    const normalizedMaxValueIndex =
        typeof maxValueIndex === "number" && Number.isFinite(maxValueIndex) ? Math.max(0, Math.trunc(maxValueIndex)) : 0;
    const normalizedCurrentBonusPoints =
        typeof currentBonusPoints === "number" && Number.isFinite(currentBonusPoints)
            ? Math.max(0, Math.trunc(currentBonusPoints))
            : 0;
    const unlockedValueIndex = getVaultBreakerUnlockedValueIndex(
        normalizedClearedCount,
        normalizedCategoryCount,
        normalizedMaxValueIndex,
        manualUnlockedValueIndex
    );

    if (
        normalizedCurrentBonusPoints >= VAULT_BREAKER_GROWING_STASH_MAX_BONUS_POINTS ||
        unlockedValueIndex < VAULT_BREAKER_GROWING_STASH_MIN_UNLOCKED_VALUE_INDEX ||
        normalizedEntryValueIndex > unlockedValueIndex
    ) {
        return 0;
    }

    const tierGap = Math.max(0, unlockedValueIndex - normalizedEntryValueIndex);
    const stashUnlockThreshold = getVaultBreakerValueUnlockThreshold(VAULT_BREAKER_GROWING_STASH_MIN_UNLOCKED_VALUE_INDEX);
    const progressSinceStashUnlock = Math.max(0, normalizedClearedCount - stashUnlockThreshold);
    const progressStepsSinceStashUnlock = Math.floor(
        progressSinceStashUnlock / VAULT_BREAKER_GROWING_STASH_PROGRESS_STEP_CLEARS
    );
    const progressChance =
        tierGap > 0 ? progressStepsSinceStashUnlock * VAULT_BREAKER_GROWING_STASH_PROGRESS_CHANCE : 0;

    return Math.min(
        VAULT_BREAKER_GROWING_STASH_MAX_CHANCE,
        VAULT_BREAKER_GROWING_STASH_BASE_CHANCE +
            tierGap * VAULT_BREAKER_GROWING_STASH_TIER_GAP_CHANCE +
            progressChance
    );
};

export interface VaultBreakerDiamondColumnCandidate {
    categoryIndex: number;
    remainingCount: number;
    roundsSinceChosen: number;
    diamondCount?: number;
}

export interface VaultBreakerDiamondColumnRollResult {
    triggered: boolean;
    categoryIndex: number | null;
    chance: number;
    weight: number;
    totalWeight: number;
    bonusPoints: number;
}

export const getVaultBreakerDiamondColumnChance = (
    categoryCandidates: readonly VaultBreakerDiamondColumnCandidate[],
    clearedCount: number
): number => {
    const normalizedClearedCount =
        typeof clearedCount === "number" && Number.isFinite(clearedCount) ? Math.max(0, Math.trunc(clearedCount)) : 0;
    const candidates = categoryCandidates.filter((candidate) => candidate.remainingCount > 0);

    if (normalizedClearedCount < VAULT_BREAKER_DIAMOND_MIN_CLEARED_COUNT || candidates.length === 0) {
        return 0;
    }

    const normalizedCandidates = candidates.map((candidate) => {
        const roundsSinceChosen =
            typeof candidate.roundsSinceChosen === "number" && Number.isFinite(candidate.roundsSinceChosen)
                ? Math.max(0, candidate.roundsSinceChosen)
                : 0;
        const diamondCount =
            typeof candidate.diamondCount === "number" && Number.isFinite(candidate.diamondCount)
                ? Math.max(0, Math.trunc(candidate.diamondCount))
                : 0;

        return {
            ...candidate,
            roundsSinceChosen,
            diamondCount,
            stackDamping: 1 + diamondCount * VAULT_BREAKER_DIAMOND_STACK_PRESSURE_DAMPING
        };
    });
    const averageRoundsSinceChosen =
        normalizedCandidates.reduce((total, candidate) => total + candidate.roundsSinceChosen, 0) /
        normalizedCandidates.length;
    const relativeNeglectTotal = normalizedCandidates.reduce(
        (total, candidate) =>
            total +
            Math.max(
                0,
                candidate.roundsSinceChosen - averageRoundsSinceChosen - VAULT_BREAKER_DIAMOND_NEGLECT_START_ROUNDS
            ) /
                candidate.stackDamping,
        0
    );
    const absoluteNeglect = normalizedCandidates.reduce(
        (highest, candidate) =>
            Math.max(
                highest,
                Math.max(0, candidate.roundsSinceChosen - VAULT_BREAKER_DIAMOND_NEGLECT_START_ROUNDS) /
                    candidate.stackDamping
            ),
        0
    );
    const neglectPressure = Math.max(relativeNeglectTotal, absoluteNeglect);

    return Math.min(
        VAULT_BREAKER_DIAMOND_MAX_CHANCE,
        VAULT_BREAKER_DIAMOND_BASE_CHANCE + neglectPressure * VAULT_BREAKER_DIAMOND_NEGLECT_CHANCE
    );
};

export const getVaultBreakerDiamondColumnWeight = (
    roundsSinceChosen: number,
    averageRoundsSinceChosen: number,
    diamondCount = 0
): number => {
    const normalizedRoundsSinceChosen =
        typeof roundsSinceChosen === "number" && Number.isFinite(roundsSinceChosen)
            ? Math.max(0, roundsSinceChosen)
            : 0;
    const normalizedAverageRoundsSinceChosen =
        typeof averageRoundsSinceChosen === "number" && Number.isFinite(averageRoundsSinceChosen)
            ? Math.max(0, averageRoundsSinceChosen)
            : 0;
    const normalizedDiamondCount =
        typeof diamondCount === "number" && Number.isFinite(diamondCount) ? Math.max(0, Math.trunc(diamondCount)) : 0;
    const neglect = Math.max(0, normalizedRoundsSinceChosen - normalizedAverageRoundsSinceChosen);

    return (
        (VAULT_BREAKER_DIAMOND_WEIGHT_BASE + Math.pow(neglect, VAULT_BREAKER_DIAMOND_NEGLECT_POWER)) /
        (1 + normalizedDiamondCount * VAULT_BREAKER_DIAMOND_STACK_WEIGHT_DAMPING)
    );
};

export const rollVaultBreakerDiamondColumn = (
    categoryCandidates: readonly VaultBreakerDiamondColumnCandidate[],
    clearedCount: number,
    random: () => number = Math.random
): VaultBreakerDiamondColumnRollResult => {
    const candidates = categoryCandidates.filter((candidate) => candidate.remainingCount > 0);
    const chance = getVaultBreakerDiamondColumnChance(candidates, clearedCount);

    if (chance <= 0 || candidates.length === 0 || random() >= chance) {
        return {
            triggered: false,
            categoryIndex: null,
            chance,
            weight: 0,
            totalWeight: 0,
            bonusPoints: 0
        };
    }

    const averageRoundsSinceChosen =
        candidates.reduce((total, candidate) => total + Math.max(0, candidate.roundsSinceChosen), 0) /
        candidates.length;
    const weightedCandidates = candidates.map((candidate) => ({
        ...candidate,
        weight: getVaultBreakerDiamondColumnWeight(
            candidate.roundsSinceChosen,
            averageRoundsSinceChosen,
            candidate.diamondCount
        )
    }));
    const totalWeight = weightedCandidates.reduce((total, candidate) => total + candidate.weight, 0);
    const pick = random() * totalWeight;
    let runningWeight = 0;

    for (const candidate of weightedCandidates) {
        runningWeight += candidate.weight;
        if (pick <= runningWeight) {
            return {
                triggered: true,
                categoryIndex: candidate.categoryIndex,
                chance,
                weight: candidate.weight,
                totalWeight,
                bonusPoints: VAULT_BREAKER_DIAMOND_BONUS_POINTS
            };
        }
    }

    const fallback = weightedCandidates[weightedCandidates.length - 1];
    return {
        triggered: Boolean(fallback),
        categoryIndex: fallback?.categoryIndex ?? null,
        chance,
        weight: fallback?.weight ?? 0,
        totalWeight,
        bonusPoints: fallback ? VAULT_BREAKER_DIAMOND_BONUS_POINTS : 0
    };
};
export type VaultBreakerChooserCandidate = {
    sessionId: string;
};

export const pickVaultBreakerChooserSessionId = (
    candidates: readonly VaultBreakerChooserCandidate[],
    options: {
        previousSessionId?: string | null;
        random?: () => number;
    } = {}
): string | null => {
    const validCandidates = candidates.filter((candidate) => candidate.sessionId.length > 0);

    if (validCandidates.length === 0) {
        return null;
    }

    const previousSessionId = options.previousSessionId ?? null;
    const eligibleCandidates =
        previousSessionId && validCandidates.length > 1
            ? validCandidates.filter((candidate) => candidate.sessionId !== previousSessionId)
            : validCandidates;
    const pool = eligibleCandidates.length > 0 ? eligibleCandidates : validCandidates;
    const rawRoll = options.random ? options.random() : Math.random();
    const normalizedRoll = Number.isFinite(rawRoll) ? Math.max(0, Math.min(0.999999, rawRoll)) : 0;
    const selected = pool[Math.floor(normalizedRoll * pool.length)] ?? pool[0] ?? null;

    return selected?.sessionId ?? null;
};

export type VaultBreakerChooserCyclePick = {
    sessionId: string | null;
    cyclePickedSessionIds: string[];
    startedNewCycle: boolean;
};

export const pickVaultBreakerChooserCycle = (
    candidates: readonly VaultBreakerChooserCandidate[],
    options: {
        cyclePickedSessionIds?: readonly string[];
        previousSessionId?: string | null;
        random?: () => number;
    } = {}
): VaultBreakerChooserCyclePick => {
    const cyclePickedSessionIds = Array.from(
        new Set(
            (options.cyclePickedSessionIds ?? []).filter(
                (sessionId) => typeof sessionId === "string" && sessionId.length > 0
            )
        )
    );
    const alreadyPicked = new Set(cyclePickedSessionIds);
    const remainingCandidates = candidates.filter((candidate) => !alreadyPicked.has(candidate.sessionId));
    const startedNewCycle = candidates.length > 0 && remainingCandidates.length === 0;
    const pool = startedNewCycle ? candidates : remainingCandidates;
    const nextCyclePickedSessionIds = startedNewCycle ? [] : cyclePickedSessionIds;
    const sessionId = pickVaultBreakerChooserSessionId(pool, {
        previousSessionId: options.previousSessionId,
        random: options.random
    });

    return {
        sessionId,
        cyclePickedSessionIds: sessionId ? [...nextCyclePickedSessionIds, sessionId] : nextCyclePickedSessionIds,
        startedNewCycle
    };
};

export const tokenizeSourceText = (sourceText: string): MazeWordToken[] =>
    normalizeSourceText(sourceText)
        .match(SOURCE_WORD_PATTERN)
        ?.map((chunk) => ({
            display: chunk,
            normalized: normalizeWordToken(chunk)
        }))
        .filter((token) => token.display.length > 0 && token.normalized.length > 0) ?? [];

export const buildRelicRunLevels = (
    sourceText: string,
    singleLineMode: boolean
): RelicRunLevelDefinition[] => {
    const normalized = normalizeSourceText(sourceText);

    if (!normalized) {
        return [];
    }

    const segments = singleLineMode
        ? [normalized]
        : normalized
              .split("\n")
              .map((line) => line.trim())
              .filter((line) => line.length > 0);

    return segments
        .map((segment) => {
            const tokens =
                segment
                    .match(SOURCE_WORD_PATTERN)
                    ?.map((chunk) => ({
                        display: chunk,
                        normalized: normalizeWordToken(chunk)
                    }))
                    .filter((token) => token.display.length > 0 && token.normalized.length > 0) ?? [];

            return {
                sourceText: segment,
                tokenCount: tokens.length,
                tokens
            } satisfies RelicRunLevelDefinition;
        })
        .filter((level) => level.tokenCount > 0);
};

const splitPictionaryEntryLine = (line: string): [string, string] | null => {
    const trimmed = line.trim();

    if (!trimmed) {
        return null;
    }

    const explicitSeparatorMatch = trimmed.match(/^(.*?)\s*[:;|]\s*(.+)$/);

    if (explicitSeparatorMatch) {
        return [explicitSeparatorMatch[1] ?? "", explicitSeparatorMatch[2] ?? ""];
    }

    const dashMatch = trimmed.match(/^(.*?)\s+[-\u2013\u2014]\s+(.+)$/);

    if (dashMatch) {
        return [dashMatch[1] ?? "", dashMatch[2] ?? ""];
    }

    const tabMatch = trimmed.match(/^(.*?)\t+(.+)$/);

    if (tabMatch) {
        return [tabMatch[1] ?? "", tabMatch[2] ?? ""];
    }

    return null;
};

const parseThreePartPictionarySourceRow = (line: string): PictionarySourceRow | null => {
    const trimmed = line.trim();

    if (!trimmed) {
        return null;
    }

    const semicolonCells = trimmed.split(";").map((cell) => cell.trim());

    if (semicolonCells.length >= 3) {
        const english = normalizePictionaryCell(semicolonCells[0] ?? "");

        if (english) {
            return {
                english,
                korean: normalizePictionaryCell(semicolonCells[1] ?? ""),
                stem: normalizePictionaryStem(semicolonCells.slice(2).join("; "))
            };
        }
    }

    const semicolonIndex = trimmed.lastIndexOf(";");

    if (semicolonIndex <= 0 || semicolonIndex >= trimmed.length - 1) {
        return null;
    }

    const pairCandidate = trimmed.slice(0, semicolonIndex).trim();
    const stemCandidate = normalizePictionaryStem(trimmed.slice(semicolonIndex + 1));

    if (!pairCandidate) {
        return null;
    }

    const pair = splitPictionaryEntryLine(pairCandidate);

    if (!pair) {
        return null;
    }

    const english = normalizePictionaryCell(pair[0] ?? "");

    if (!english) {
        return null;
    }

    return {
        english,
        korean: normalizePictionaryCell(pair[1] ?? ""),
        stem: stemCandidate
    };
};

const parseStructuredPictionarySourceRow = (line: string): PictionarySourceRow | null => {
    const trimmed = line.trim();

    if (!trimmed || !trimmed.includes("\t")) {
        return null;
    }

    const cells = trimmed.split("\t");
    const english = normalizePictionaryCell(cells[0] ?? "");

    if (!english) {
        return null;
    }

    return {
        english,
        korean: normalizePictionaryCell(cells[1] ?? ""),
        stem: normalizePictionaryStem(cells.slice(2).join(" "))
    };
};

type ParsedPictionarySourceLine = {
    row: PictionarySourceRow;
    canAttachStemLine: boolean;
};

const parsePictionarySourceLine = (
    line: string,
    fallbackStem: string
): ParsedPictionarySourceLine | null => {
    const threePartRow = parseThreePartPictionarySourceRow(line);

    if (threePartRow) {
        return {
            row: threePartRow,
            canAttachStemLine: false
        };
    }

    const structuredRow = parseStructuredPictionarySourceRow(line);

    if (structuredRow) {
        return {
            row: structuredRow,
            canAttachStemLine: structuredRow.stem.length === 0
        };
    }

    const split = splitPictionaryEntryLine(line);

    if (split) {
        return {
            row: {
                english: normalizePictionaryCell(split[0] ?? ""),
                korean: normalizePictionaryCell(split[1] ?? ""),
                stem: fallbackStem
            },
            canAttachStemLine: true
        };
    }

    const english = normalizePictionaryCell(line);

    if (!english) {
        return null;
    }

    return {
        row: {
            english,
            korean: "",
            stem: fallbackStem
        },
        canAttachStemLine: false
    };
};

const isLikelyPictionaryWorksheetStemLine = (line: string): boolean => {
    const stem = normalizePictionaryStem(line);

    if (!stem) {
        return false;
    }

    const hasBlank = /_{2,}/.test(stem);
    const hasSentenceEnding = /[.?!]["')\]]*$/.test(stem);
    const looksLikeEntryRow = Boolean(parseStructuredPictionarySourceRow(line) || splitPictionaryEntryLine(line));
    const wordCount = stem.split(/\s+/).filter(Boolean).length;

    // Complete entries take precedence over worksheet clues, even when they
    // contain a sentence ending or a blank. Never consume the next source row.
    return !looksLikeEntryRow && (hasBlank || hasSentenceEnding || wordCount >= 3);
};

const findNextPictionarySourceLineIndex = (
    lines: readonly string[],
    startIndex: number
): number | null => {
    for (let index = startIndex; index < lines.length; index += 1) {
        if ((lines[index] ?? "").trim().length > 0) {
            return index;
        }
    }

    return null;
};

export const parsePictionarySourceRows = (
    sourceText: string,
    options: Pick<PictionaryBuildOptions, "fallbackStem"> = {}
): PictionarySourceRow[] => {
    const fallbackStem = normalizePictionaryStem(options.fallbackStem ?? "");
    const lines = normalizeSourceText(sourceText).split("\n");
    const rows: PictionarySourceRow[] = [];

    for (let index = 0; index < lines.length; index += 1) {
        const parsed = parsePictionarySourceLine(lines[index] ?? "", fallbackStem);

        if (!parsed) {
            continue;
        }

        if (parsed.canAttachStemLine) {
            const nextLineIndex = findNextPictionarySourceLineIndex(lines, index + 1);
            const nextLine = nextLineIndex === null ? "" : lines[nextLineIndex] ?? "";

            if (nextLineIndex !== null && isLikelyPictionaryWorksheetStemLine(nextLine)) {
                rows.push({
                    ...parsed.row,
                    stem: normalizePictionaryStem(nextLine)
                });
                index = nextLineIndex;
                continue;
            }
        }

        rows.push(parsed.row);
    }

    return rows;
};

export const getPictionarySourceEntryLineIndexes = (
    sourceText: string,
    options: Pick<PictionaryBuildOptions, "fallbackStem"> = {}
): number[] => {
    const fallbackStem = normalizePictionaryStem(options.fallbackStem ?? "");
    const lines = sourceText
        .replace(/\r\n?/g, "\n")
        .slice(0, MAX_SOURCE_TEXT_LENGTH)
        .split("\n");
    const lineIndexes: number[] = [];

    for (let index = 0; index < lines.length; index += 1) {
        const parsed = parsePictionarySourceLine(lines[index] ?? "", fallbackStem);

        if (!parsed) {
            continue;
        }

        lineIndexes.push(index);

        if (parsed.canAttachStemLine) {
            const nextLineIndex = findNextPictionarySourceLineIndex(lines, index + 1);
            const nextLine = nextLineIndex === null ? "" : lines[nextLineIndex] ?? "";

            if (nextLineIndex !== null && isLikelyPictionaryWorksheetStemLine(nextLine)) {
                index = nextLineIndex;
            }
        }
    }

    return lineIndexes;
};

export const serializePictionarySourceRows = (rows: readonly PictionarySourceRow[]): string =>
    normalizeSourceText(
        rows
            .filter((row) => normalizePictionaryCell(row.english).length > 0)
            .map((row) =>
                [
                    normalizePictionaryCell(row.english),
                    normalizePictionaryCell(row.korean),
                    normalizePictionaryStem(row.stem)
                ].join("\t")
            )
            .join("\n")
    );

export const getSharedPictionaryStem = (
    rows: readonly PictionarySourceRow[],
    fallbackStem = DEFAULT_PICTIONARY_STEM
): string =>
    rows.map((row) => normalizePictionaryStem(row.stem)).find((stem) => stem.length > 0) ??
    (normalizePictionaryStem(fallbackStem) || DEFAULT_PICTIONARY_STEM);

export const parsePictionaryEntryLine = (
    line: string,
    options: PictionaryBuildOptions = {}
): PictionaryEntryDefinition | null => {
    const sourceRow = parsePictionarySourceRows(line, {
        fallbackStem: options.fallbackStem
    })[0];

    if (!sourceRow) {
        return null;
    }

    const english = normalizePictionaryCell(sourceRow.english);
    const korean = normalizePictionaryCell(sourceRow.korean);
    const stem = options.noHints
        ? ""
        : options.sameStem
          ? getSharedPictionaryStem([sourceRow], options.fallbackStem)
          : normalizePictionaryStem(sourceRow.stem);
    const normalizedEnglish = normalizeLooseGuess(english);

    if (!english || !normalizedEnglish) {
        return null;
    }

    return {
        english,
        korean,
        stem,
        normalizedEnglish
    };
};

export const buildPictionaryEntries = (
    sourceText: string,
    options: PictionaryBuildOptions = {}
): PictionaryEntryDefinition[] => {
    const rows = parsePictionarySourceRows(sourceText, {
        fallbackStem: options.fallbackStem
    });
    const sharedStem = getSharedPictionaryStem(rows, options.fallbackStem);

    return rows
        .map((row) => {
            const english = normalizePictionaryCell(row.english);
            const korean = normalizePictionaryCell(row.korean);
            const stem = options.noHints
                ? ""
                : options.sameStem
                  ? sharedStem
                  : normalizePictionaryStem(row.stem);
            const normalizedEnglish = normalizeLooseGuess(english);

            if (!english || !normalizedEnglish) {
                return null;
            }

            return {
                english,
                korean,
                stem,
                normalizedEnglish
            } satisfies PictionaryEntryDefinition;
        })
        .filter((entry): entry is PictionaryEntryDefinition => Boolean(entry));
};

const normalizeFinalResultScore = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) && value > 0
        ? Math.trunc(value)
        : null;

const normalizeFinalResultStandingScore = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) && value >= 0
        ? Math.trunc(value)
        : null;

const normalizeFinalResultElapsedMs = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) && value >= 0
        ? Math.round(value)
        : null;

export const buildFinalResultStandings = (
    mode: RoomMode | null,
    candidates: readonly FinalResultCandidateView[]
): FinalResultStandingView[] => {
    if (mode !== RELIC_RUN_MODE && mode !== PICTURE_DETECTIVES_MODE && mode !== VAULT_BREAKERS_MODE && mode !== PLANET_HOPPERS_MODE) {
        return [];
    }

    const rankedCandidates = candidates
        .map((candidate) => {
            const rankValue =
                mode === RELIC_RUN_MODE
                    ? normalizeFinalResultElapsedMs(candidate.elapsedMs)
                    : normalizeFinalResultStandingScore(candidate.score);

            return {
                candidate,
                rankValue
            };
        })
        .sort((left, right) => {
            if (left.rankValue === null && right.rankValue !== null) {
                return 1;
            }

            if (left.rankValue !== null && right.rankValue === null) {
                return -1;
            }

            const rankDelta =
                left.rankValue !== null && right.rankValue !== null
                    ? mode === RELIC_RUN_MODE
                        ? left.rankValue - right.rankValue
                        : right.rankValue - left.rankValue
                    : 0;

            return (
                rankDelta ||
                left.candidate.name.localeCompare(right.candidate.name) ||
                (left.candidate.sortIndex ?? 0) - (right.candidate.sortIndex ?? 0) ||
                left.candidate.sessionId.localeCompare(right.candidate.sessionId)
            );
        });

    const standings: FinalResultStandingView[] = [];
    let currentRankValue: number | null = null;
    let currentPlacement = 0;

    for (const rankedCandidate of rankedCandidates) {
        if (rankedCandidate.rankValue !== null && rankedCandidate.rankValue !== currentRankValue) {
            currentPlacement += 1;
            currentRankValue = rankedCandidate.rankValue;
        }

        standings.push({
            sessionId: rankedCandidate.candidate.sessionId,
            name: rankedCandidate.candidate.name,
            connected: rankedCandidate.candidate.connected,
            placement: rankedCandidate.rankValue === null ? null : currentPlacement,
            score: normalizeFinalResultStandingScore(rankedCandidate.candidate.score),
            elapsedMs: normalizeFinalResultElapsedMs(rankedCandidate.candidate.elapsedMs)
        });
    }

    return standings;
};

export const buildFinalResultGroups = (
    mode: RoomMode | null,
    candidates: readonly FinalResultCandidateView[]
): FinalResultGroupView[] => {
    if (mode !== RELIC_RUN_MODE && mode !== PICTURE_DETECTIVES_MODE && mode !== VAULT_BREAKERS_MODE && mode !== PLANET_HOPPERS_MODE) {
        return [];
    }

    const rankedCandidates = candidates
        .map((candidate) => {
            const rankValue =
                mode === RELIC_RUN_MODE
                    ? normalizeFinalResultElapsedMs(candidate.elapsedMs)
                    : normalizeFinalResultScore(candidate.score);

            return rankValue === null
                ? null
                : {
                      candidate,
                      rankValue
                  };
        })
        .filter(
            (
                candidate
            ): candidate is {
                candidate: FinalResultCandidateView;
                rankValue: number;
            } => candidate !== null
        )
        .sort((left, right) => {
            const rankDelta =
                mode === RELIC_RUN_MODE
                    ? left.rankValue - right.rankValue
                    : right.rankValue - left.rankValue;

            return (
                rankDelta ||
                left.candidate.name.localeCompare(right.candidate.name) ||
                (left.candidate.sortIndex ?? 0) - (right.candidate.sortIndex ?? 0)
            );
        });

    const groups: FinalResultGroupView[] = [];

    for (const rankedCandidate of rankedCandidates) {
        const currentGroup = groups[groups.length - 1] ?? null;

        if (currentGroup) {
            const currentRankValue =
                mode === RELIC_RUN_MODE ? currentGroup.elapsedMs : currentGroup.score;

            if (currentRankValue === rankedCandidate.rankValue) {
                currentGroup.players.push({
                    sessionId: rankedCandidate.candidate.sessionId,
                    name: rankedCandidate.candidate.name,
                    connected: rankedCandidate.candidate.connected,
                    score: normalizeFinalResultScore(rankedCandidate.candidate.score),
                    elapsedMs: normalizeFinalResultElapsedMs(rankedCandidate.candidate.elapsedMs)
                });
                continue;
            }
        }

        if (groups.length >= 3) {
            break;
        }

        groups.push({
            placement: (groups.length + 1) as PlayerRosterPlacement,
            score: mode === PICTURE_DETECTIVES_MODE || mode === VAULT_BREAKERS_MODE || mode === PLANET_HOPPERS_MODE ? rankedCandidate.rankValue : null,
            elapsedMs: mode === RELIC_RUN_MODE ? rankedCandidate.rankValue : null,
            players: [
                {
                    sessionId: rankedCandidate.candidate.sessionId,
                    name: rankedCandidate.candidate.name,
                    connected: rankedCandidate.candidate.connected,
                    score: normalizeFinalResultScore(rankedCandidate.candidate.score),
                    elapsedMs: normalizeFinalResultElapsedMs(rankedCandidate.candidate.elapsedMs)
                }
            ]
        });
    }

    return groups;
};

export const getModeLabel = (mode: RoomMode | null): string | null =>
    mode === PLANET_HOPPERS_MODE ? "Planet Hoppers" : mode === RELIC_RUN_MODE
        ? RELIC_RUN_LABEL
        : mode === PICTURE_DETECTIVES_MODE
          ? PICTURE_DETECTIVES_LABEL
          : mode === VAULT_BREAKERS_MODE
            ? VAULT_BREAKERS_LABEL
          : null;

export const getModeDescription = (mode: RoomMode | null): string | null =>
    mode === PLANET_HOPPERS_MODE ? "Choose your planet. Scan, launch, and discover the answer together." : mode === RELIC_RUN_MODE
        ? RELIC_RUN_DESCRIPTION
        : mode === PICTURE_DETECTIVES_MODE
          ? PICTURE_DETECTIVES_DESCRIPTION
          : mode === VAULT_BREAKERS_MODE
            ? VAULT_BREAKERS_DESCRIPTION
          : null;
